/**
 * Staff POS Checkout Page
 *
 * "65-year-old easy" design:
 * - Large touch targets (minimum 48px)
 * - High contrast colors
 * - Clear, readable text (18px+ base)
 * - Simple, intuitive layout
 * - Minimal cognitive load
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';
import {
  posAPI,
  POSProduct,
  POSCategory,
  POSTransaction,
  POSSession,
  POSReceipt,
  POSRefundReceipt,
  POSCustomer,
  POSFeedBin,
  PettyCashWithdrawal,
  TillCloseReport,
  POSRegisterInfo,
} from '../../services/storeAPI';
import { printReceipt, printRefundReceipt, printTillReport, openCashDrawer } from '../../services/receiptPrinter';
import { getCategoryColor } from '../../constants/posColors';
import POSCategoryNav, { NavState, NavLevel } from './POSCategoryNav';
import POSProductGrid from './POSProductGrid';
import { CartItemQuantityControl } from './CartItemQuantityControl';
import { BagCreditBadge, CartBagCreditLine } from '../../components/Store/CartBagCredit';
import { TabBanner } from '../../components/Store/TabBanner';
import { ParkedCartsTray, ParkedCart } from '../../components/Store/ParkedCartsTray';
import { ShiftCloseParkedPrompt } from '../../components/Store/ShiftCloseParkedPrompt';
import POSInvoicePanel from './POSInvoicePanel';
import BridgeClubPromptModal from '../../components/Store/BridgeClubPromptModal';
import { FEED_CATEGORY_FAMILIES, getAvailableAnimalTypes } from '../../constants/posCategoryFamilies';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { buildBinLoadSelections, LineBinSelection } from './binLoadSelections';

type ViewMode = 'checkout' | 'search' | 'payment' | 'receipt' | 'refund';
type PaymentMethod = 'cash' | 'card' | 'check';

// Bin loading state for payment screen
// Bin-load selections are per CART LINE (see binLoadSelections.ts), one bin each.

// Session Summary type
interface SessionSummary {
  session_id: number;
  cashier: string;
  opened_at: string;
  status: string;
  opening_cash: number;
  transaction_count: number;
  totals: {
    sales: number;
    tax: number;
    surcharge: number;
    discounts: number;
  };
  payment_breakdown: {
    cash: number;
    credit: number;
    debit: number;
    check: number;
    account?: number;
  };
  tab_paydowns?: number;
  expected_cash: number;
  voided_count: number;
  refund_count?: number;
  refund_total?: number;
  opening_check_float?: number;
  expected_checks?: number;
  check_count?: number;
  petty_cash_total?: number;
  petty_cash_count?: number;
}

// CartQuantityInput + the cart-line quantity control now live in
// ./CartItemQuantityControl (extracted so the weight_tracked read-only
// behaviour is unit-tested). Imported at the top of this file.

const StorePOS: React.FC = () => {
  const { user, isAuthenticated, logout } = useStoreAuth();
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Session state
  const [session, setSession] = useState<POSSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [showOpenSessionModal, setShowOpenSessionModal] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [openingCheckFloat, setOpeningCheckFloat] = useState('');
  const [registers, setRegisters] = useState<POSRegisterInfo[]>([]);
  const [selectedRegisterId, setSelectedRegisterId] = useState<number | null>(null);

  // Transaction state
  const [transaction, setTransaction] = useState<POSTransaction | null>(null);

  // Product state
  const [quickButtons, setQuickButtons] = useState<POSProduct[]>([]);
  const [categories, setCategories] = useState<POSCategory[]>([]);
  const [products, setProducts] = useState<POSProduct[]>([]);
  const [searchResults, setSearchResults] = useState<POSProduct[]>([]);
  // Search box stays responsive: `searchQuery` updates per keystroke (bound to the
  // input), while the debounced value drives the product filter so typing never blocks.
  // `flushSearch` bypasses the delay for barcode scans (results should appear at once).
  const { value: searchQuery, setValue: setSearchQuery, debounced: debouncedQuery, flush: flushSearch } =
    useDebouncedSearch('', 250);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);

  // Hierarchical category navigation state
  const [navState, setNavState] = useState<NavState>({
    level: 'family',
    selectedFamily: null,
    selectedCategory: null,
    selectedAnimalType: null,
  });
  const [allProducts, setAllProducts] = useState<POSProduct[]>([]);

  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('checkout');
  const [error, setError] = useState('');
  const [bagCreditNotice, setBagCreditNotice] = useState<string | null>(null);
  const [parkedCarts, setParkedCarts] = useState<ParkedCart[]>([]);
  const [loading, setLoading] = useState(false);

  // Auto-hide header & fullscreen state
  const [headerVisible, setHeaderVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const headerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [cashTendered, setCashTendered] = useState('');
  const [changeDue, setChangeDue] = useState<number | null>(null);
  const [cardPaymentId, setCardPaymentId] = useState<number | null>(null);
  const [cardPaymentStatus, setCardPaymentStatus] = useState<string>('');
  const [cardPaymentApproved, setCardPaymentApproved] = useState(false);
  const [cardIsCredit, setCardIsCredit] = useState<boolean | null>(null);

  // Decline + polling-timeout state (P3 — silent-overcharge follow-up).
  // Tracked with a ref so the setTimeout closure always reads the latest
  // start time without re-binding the polling loop.
  const [cardDeclineCount, setCardDeclineCount] = useState(0);
  const [lastDeclineReason, setLastDeclineReason] = useState('');
  const pollStartTimeRef = useRef<number | null>(null);
  const MAX_CARD_POLL_MS = 90_000; // 90s — Helcim terminals typically prompt for ~60s

  // Split-payment state (P4). Backend has always supported multiple
  // POSPayment rows per transaction; we just expose it. amountDue is the
  // server-truth remaining balance, refreshed after every successful tender.
  const [splitMode, setSplitMode] = useState(false);
  const [amountDue, setAmountDue] = useState<number>(0);
  const [splitPayments, setSplitPayments] = useState<Array<{
    id: number;
    method: string;
    methodDisplay: string;
    amount: number;
    cardLastFour?: string | null;
    checkNumber?: string | null;
    healedFromEmail?: boolean;
  }>>([]);
  const [splitAmountInput, setSplitAmountInput] = useState('');

  // Purchase-history drawer state (POS attach drawer). Cached per
  // selected customer so reopening the drawer doesn't re-hit the API.
  // Cache key shape: `m:<memberId>` or `v:<visitorId>`. The
  // purchaseHistoryKey/openPurchaseDrawer/toggleBasketKey closures live
  // below selectedCustomer's useState (they reference it at call time).
  const [showPurchaseDrawer, setShowPurchaseDrawer] = useState(false);
  const [purchaseHistoryCache, setPurchaseHistoryCache] = useState<Record<string, any>>({});
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);
  // Per-key error state so a silent fetch failure surfaces in the drawer
  // instead of masquerading as "No data yet." Cleared on retry.
  const [purchaseHistoryError, setPurchaseHistoryError] = useState<Record<string, string>>({});
  const [expandedBasketKeys, setExpandedBasketKeys] = useState<Set<string>>(new Set());

  // Receipt state
  const [receipt, setReceipt] = useState<POSReceipt | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState('');

  // Discount modal state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountTarget, setDiscountTarget] = useState<'item' | 'cart'>('cart');
  const [discountItemId, setDiscountItemId] = useState<number | null>(null);
  const [discountType, setDiscountType] = useState<'percent' | 'dollar'>('percent');
  const [discountValue, setDiscountValue] = useState('');

  // Reservation/oversell guard block (the "Charles" set). When a payment is
  // blocked (409), the cashier either counts in uncounted stock ('oversell')
  // or does a deliberate manager override ('reserved'), then the same payment
  // retries. retry(allowOverride) re-runs the originating payment handler.
  const [guardBlock, setGuardBlock] = useState<{
    code: string; product: string; productId: number | null;
    requested: number; available: number;
    retry: (allowOverride: boolean) => Promise<void>;
  } | null>(null);
  const [guardAddQty, setGuardAddQty] = useState('');
  const [guardBusy, setGuardBusy] = useState(false);

  // BYOC/Variable price weight entry
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightProduct, setWeightProduct] = useState<POSProduct | null>(null);
  const [weightValue, setWeightValue] = useState('');
  // weight_tracked tag-weight prompt — SEPARATE from the BYOC/variable-price weight
  // modal above so the BYOC path stays byte-for-byte unchanged (no bin, no price
  // scaling, fixed price, one line per package).
  const [showTagWeightModal, setShowTagWeightModal] = useState(false);
  const [tagWeightProduct, setTagWeightProduct] = useState<POSProduct | null>(null);
  const [tagWeightValue, setTagWeightValue] = useState('');
  const [priceValue, setPriceValue] = useState('');

  // Customer lookup state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  // Bridge Club signup nudge shown at Pay time on unattributed walk-ins.
  const [showBcPrompt, setShowBcPrompt] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<POSCustomer[]>([]);
  // Bridge Club invite from POS for selected UnmatchedVisitor.
  const [showInviteInput, setShowInviteInput] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  // Link-mode: when set, the customer modal links the selected EXISTING
  // member to this walk-in's UnmatchedVisitor instead of attaching it.
  const [linkWalkinVisitorId, setLinkWalkinVisitorId] = useState<number | null>(null);
  const [linkMessage, setLinkMessage] = useState<string | null>(null);
  // "Add new Bridge Club customer" inline-form state.
  // Opened from the zero-results affordance in the customer modal.
  // Pre-fills from the current search query (smart: email/phone/name).
  const [showNewBcForm, setShowNewBcForm] = useState(false);
  const [newBcFirstName, setNewBcFirstName] = useState('');
  const [newBcLastName, setNewBcLastName] = useState('');
  const [newBcEmail, setNewBcEmail] = useState('');
  const [newBcPhone, setNewBcPhone] = useState('');
  const [newBcSubmitting, setNewBcSubmitting] = useState(false);
  const [newBcError, setNewBcError] = useState<string | null>(null);
  // Existing-member returned from backend on 409 duplicate_email or
  // 200 warning='phone_match'. Rendered with "Use this member" button.
  const [newBcExistingMember, setNewBcExistingMember] = useState<POSCustomer | null>(null);
  const [newBcPhoneCandidates, setNewBcPhoneCandidates] = useState<POSCustomer[]>([]);
  // Local-points redemption at POS.
  const [redeemingPoints, setRedeemingPoints] = useState(false);
  const [redeemMessage, setRedeemMessage] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<POSCustomer | null>(null);
  // Invoice collection (A1): target of the open-invoices panel — a BC
  // member (memberId) or a wholesale Customer row (customerId).
  const [invoicePanelTarget, setInvoicePanelTarget] = useState<
    { memberId?: number; customerId?: number; name: string } | null
  >(null);
  const [customerBins, setCustomerBins] = useState<POSFeedBin[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);

  // Purchase-history drawer derived state — declared here so it sees
  // selectedCustomer's useState above. Re-evaluated each render so an
  // attach swap clears the lookup key cleanly.
  const purchaseHistoryKey = (() => {
    if (!selectedCustomer) return null;
    if (selectedCustomer.is_unmatched && selectedCustomer.unmatched_visitor_id) {
      return `v:${selectedCustomer.unmatched_visitor_id}`;
    }
    if (selectedCustomer.id) {
      return `m:${selectedCustomer.id}`;
    }
    return null;
  })();
  const purchaseHistory: any = purchaseHistoryKey
    ? purchaseHistoryCache[purchaseHistoryKey] || null
    : null;

  const openPurchaseDrawer = async () => {
    if (!selectedCustomer || !purchaseHistoryKey) return;
    setShowPurchaseDrawer(true);
    if (purchaseHistoryCache[purchaseHistoryKey]) return;
    setPurchaseHistoryLoading(true);
    // Clear any prior error for this key so a retry actually retries.
    setPurchaseHistoryError((prev) => {
      const next = { ...prev };
      delete next[purchaseHistoryKey];
      return next;
    });
    try {
      const data: any = await posAPI.getPurchaseHistory({
        memberId: selectedCustomer.is_unmatched ? null : selectedCustomer.id,
        visitorId: selectedCustomer.is_unmatched ? selectedCustomer.unmatched_visitor_id : null,
      });
      setPurchaseHistoryCache((prev) => ({ ...prev, [purchaseHistoryKey]: data }));
    } catch (err: any) {
      // Surface the error in the drawer instead of silently showing
      // "No data yet." — that masked a backend NameError for ~hours
      // on 2026-05-16. Anything that isn't an empty-success result
      // must be visible to the cashier.
      console.warn('Purchase history fetch failed:', err);
      const message = err?.message || 'Could not load purchase history.';
      setPurchaseHistoryError((prev) => ({ ...prev, [purchaseHistoryKey]: message }));
    } finally {
      setPurchaseHistoryLoading(false);
    }
  };

  const toggleBasketKey = (key: string) => {
    setExpandedBasketKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Bin loading state (for payment screen)
  const [binLoadSelections, setBinLoadSelections] = useState<LineBinSelection[]>([]);
  const [printReceipt_, setPrintReceipt_] = useState(true);
  const [emailReceipt, setEmailReceipt] = useState(false);
  const [binLoadResults, setBinLoadResults] = useState<Array<{binName: string; addedLbs: number; newLevel: number; fillPct: number}>>([]);

  // Shift closeout state
  const [showCloseoutModal, setShowCloseoutModal] = useState(false);
  // End-shift "keep or discard parked carts?" prompt (option C).
  const [showParkedPrompt, setShowParkedPrompt] = useState(false);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
  const [closingCash, setClosingCash] = useState('');
  const [closingChecks, setClosingChecks] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [checkNumber, setCheckNumber] = useState('');

  // Petty cash state
  const [showPettyCashModal, setShowPettyCashModal] = useState(false);
  const [pettyCashAmount, setPettyCashAmount] = useState('');
  const [pettyCashCategory, setPettyCashCategory] = useState('supplies');
  const [pettyCashDescription, setPettyCashDescription] = useState('');
  const [pettyCashLoading, setPettyCashLoading] = useState(false);
  const [pettyCashError, setPettyCashError] = useState('');
  const [pettyCashDrawerAmount, setPettyCashDrawerAmount] = useState<number | null>(null);
  const [pettyCashWithdrawals, setPettyCashWithdrawals] = useState<PettyCashWithdrawal[]>([]);

  // Refund state
  const [refundTxnNumber, setRefundTxnNumber] = useState('');
  const [refundLookupData, setRefundLookupData] = useState<any>(null);
  const [refundLookupError, setRefundLookupError] = useState('');
  const [refundLookupLoading, setRefundLookupLoading] = useState(false);
  const [refundSelectedItems, setRefundSelectedItems] = useState<Set<number>>(new Set());
  const [refundReason, setRefundReason] = useState('');
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [refundCustomAmount, setRefundCustomAmount] = useState('');
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundResult, setRefundResult] = useState<any>(null);
  const [refundReceipt, setRefundReceipt] = useState<POSRefundReceipt | null>(null);
  // Card-present debit refund: customer taps at the terminal; we poll status.
  const [refundTapMessage, setRefundTapMessage] = useState<string>('');

  // Mill request popup state
  const [showMillRequestPopup, setShowMillRequestPopup] = useState(false);
  const [millRequestCount, setMillRequestCount] = useState(0);
  const [millRequestUrgent, setMillRequestUrgent] = useState(0);

  // Define loader functions first (before useEffects that depend on them)
  const loadSession = async () => {
    try {
      setSessionLoading(true);
      const [sessionResponse, registerResponse] = await Promise.all([
        posAPI.getCurrentSession(),
        posAPI.getRegisters(),
      ]);
      const regs = registerResponse.registers || [];
      setRegisters(regs);
      if (regs.length === 1) setSelectedRegisterId(regs[0].id);
      if (sessionResponse.session) {
        setSession(sessionResponse.session);
        // Resume-by-txn-number: if the URL carries ?txn=YYYYMMDD-NNNN
        // (set by the "Load in POS" buttons on BC orders + wholesale
        // Will Call orders), try to load that pre-staged transaction
        // instead of minting a fresh empty one. This was the missing
        // wiring that made the BC "Load in POS" pattern look broken —
        // server created the txn, but the cashier landed on a new
        // empty one and the staged cart was orphaned.
        const requestedTxn = new URLSearchParams(window.location.search).get('txn');
        let loaded = false;
        if (requestedTxn) {
          try {
            const resumeResponse = await posAPI.getTransactionByNumber(requestedTxn);
            setTransaction(resumeResponse.transaction);
            setViewMode('checkout');
            loaded = true;
          } catch (resumeErr) {
            // Fall through to fresh-txn creation. Log but don't block
            // — the cashier may have refreshed the page after the txn
            // completed, in which case a fresh txn is the right move.
            console.warn(
              `Could not resume transaction ${requestedTxn}; creating new:`,
              resumeErr,
            );
          }
        }
        if (!loaded) {
          // Persistent cart: restore the cashier's active cart across navigation
          // (they left to check a locker/mill order and came back) instead of
          // wiping it with a fresh empty one. The full cart lives server-side.
          const savedTxn = sessionStorage.getItem('pos_active_txn');
          if (savedTxn) {
            try {
              const resumeResponse = await posAPI.getTransactionByNumber(savedTxn);
              if (resumeResponse.transaction?.status === 'in_progress') {
                setTransaction(resumeResponse.transaction);
                setViewMode('checkout');
                loaded = true;
              }
            } catch {
              sessionStorage.removeItem('pos_active_txn');
            }
          }
        }
        if (!loaded) {
          // Auto-start a new transaction so POS is ready to sell immediately
          try {
            const txnResponse = await posAPI.createTransaction();
            setTransaction(txnResponse.transaction);
            setViewMode('checkout');
          } catch (txnErr) {
            console.error('Failed to auto-create transaction:', txnErr);
          }
        }
      } else {
        setShowOpenSessionModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load session');
    } finally {
      setSessionLoading(false);
    }
  };

  const loadQuickButtons = async () => {
    try {
      const response = await posAPI.getQuickButtons();
      setQuickButtons(response.products || []);
    } catch (err) {
      console.error('Failed to load quick buttons:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await posAPI.getCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadProducts = useCallback(async (categoryId: number | null = null) => {
    try {
      setProductsLoading(true);
      const response = await posAPI.getProducts(categoryId);
      setProducts(response.products || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // Load all products for hierarchical navigation
  const loadAllProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      // Load all products (increase limit from default 100)
      const response = await posAPI.getProducts(null, 500);
      setAllProducts(response.products || []);
    } catch (err) {
      console.error('Failed to load all products:', err);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // Load session and products on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadSession();
    loadQuickButtons();
    loadCategories();
    loadAllProducts(); // Load all products for hierarchical nav
  }, [isAuthenticated, navigate, loadAllProducts]);

  // Check for pending mill requests on mount (morning popup)
  useEffect(() => {
    const checkMillRequests = async () => {
      // Check if already dismissed today
      const lastDismiss = localStorage.getItem('lastMillRequestDismiss');
      const today = new Date().toISOString().split('T')[0];
      if (lastDismiss === today) return;

      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const res = await fetch(`${API_BASE}/store/mill-requests/count/`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.urgent_count > 0) {
            setMillRequestCount(data.count);
            setMillRequestUrgent(data.urgent_count);
            setShowMillRequestPopup(true);
          }
        }
      } catch {
        // Silently ignore — endpoint may not exist yet
      }
    };
    if (isAuthenticated) checkMillRequests();
  }, [isAuthenticated]);

  const dismissMillRequestPopup = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('lastMillRequestDismiss', today);
    setShowMillRequestPopup(false);
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Sync fullscreen state when user presses Esc
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Hierarchical navigation handlers
  const handleSelectFamily = (family: string) => {
    setError(''); // Clear any errors when navigating
    setNavState({
      level: 'category',
      selectedFamily: family,
      selectedCategory: null,
      selectedAnimalType: null,
    });
  };

  const handleSelectCategory = (category: string) => {
    setError(''); // Clear any errors when navigating
    // For feed categories (5 STAR, PLUS, ESS), show animal type level
    // For other categories (Pet, Specialty, etc.), just show products directly
    if (navState.selectedFamily && FEED_CATEGORY_FAMILIES.includes(navState.selectedFamily)) {
      setNavState({
        ...navState,
        level: 'animal',
        selectedCategory: category,
      });
    } else {
      // Non-feed categories - just filter by category, no animal type
      setNavState({
        ...navState,
        selectedCategory: category,
      });
    }
  };

  const handleSelectAnimalType = (animalType: string) => {
    setError(''); // Clear any errors when navigating
    setNavState({
      ...navState,
      selectedAnimalType: animalType,
    });
  };

  const handleNavBack = () => {
    setError(''); // Clear any errors when navigating
    if (navState.level === 'animal') {
      setNavState({
        ...navState,
        level: 'category',
        selectedCategory: null,
        selectedAnimalType: null,
      });
    } else if (navState.level === 'category') {
      setNavState({
        level: 'family',
        selectedFamily: null,
        selectedCategory: null,
        selectedAnimalType: null,
      });
    }
  };

  const handleShowAllProducts = () => {
    setError(''); // Clear any errors when navigating
    setNavState({
      level: 'family',
      selectedFamily: null,
      selectedCategory: null,
      selectedAnimalType: null,
    });
  };

  // Calculate available animal types based on products in selected category
  const availableAnimalTypes = useMemo(() => {
    if (!navState.selectedCategory) return ['All'];
    return getAvailableAnimalTypes(allProducts, navState.selectedCategory);
  }, [allProducts, navState.selectedCategory]);

  // NOTE: Removed auto-focus on barcode input to prevent keyboard popup on touchscreens
  // Hardware barcode scanners work by sending keyboard events without needing focus
  // Users can tap the input field when they want to type manually

  const handleOpenSession = async () => {
    try {
      setLoading(true);
      const cash = parseFloat(openingCash) || 0;
      const checkFloat = parseFloat(openingCheckFloat) || 0;
      const response = await posAPI.openSession(cash, checkFloat, 'POS-1', selectedRegisterId || undefined);
      setSession(response.session);
      setShowOpenSessionModal(false);
      setOpeningCash('');
      setOpeningCheckFloat('');

      // Auto-start a new transaction so POS is ready to sell immediately
      try {
        const txnResponse = await posAPI.createTransaction();
        setTransaction(txnResponse.transaction);
        setViewMode('checkout');
      } catch (txnErr) {
        console.error('Failed to auto-create transaction:', txnErr);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to open session');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await posAPI.createTransaction();
      setTransaction(response.transaction);
      setViewMode('checkout');
      setChangeDue(null);
      setReceipt(null);
    } catch (err: any) {
      setError(err.message || 'Failed to start transaction');
    } finally {
      setLoading(false);
    }
  };

  // Barcode scanning (USB HID scanners send keystrokes)
  // Also supports text search - if no exact barcode match, falls back to name/SKU search
  const handleBarcodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = barcodeInput.trim();
    if (!code) return;

    try {
      setLoading(true);

      // Check if this is a member code (BC:{id} format or starts with common member prefixes)
      const isMemberCode = code.toUpperCase().startsWith('BC:') ||
                           code.toUpperCase().startsWith('MEMBER:') ||
                           /^[A-Z]{2,3}\d{6,}$/.test(code); // Common member barcode pattern

      if (isMemberCode) {
        // Try to lookup member by code
        try {
          const memberResponse = await posAPI.lookupMemberByCode(code);
          if (memberResponse.member) {
            await handleSelectCustomer(memberResponse.member);
            playBeep(true);
            setBarcodeInput('');
            setLoading(false);
            // Don't auto-focus - prevents keyboard popup on touchscreens
            return;
          }
        } catch {
          // Not a member code, try product lookup
        }
      }

      // Try exact barcode/SKU/UPC lookup first
      try {
        const response = await posAPI.lookupBarcode(code);
        if (response.product) {
          await addProductToCart(response.product);
          setBarcodeInput('');
          setLoading(false);
          return;
        }
      } catch {
        // No exact barcode match - fall through to text search
      }

      // Fall back to text search (partial name/SKU matching)
      const searchTerm = code.toLowerCase();
      const matches = allProducts.filter(p =>
        p.name.toLowerCase().includes(searchTerm) ||
        (p.sku && p.sku.toLowerCase().includes(searchTerm)) ||
        (p.barcode && p.barcode.toLowerCase().includes(searchTerm))
      );

      if (matches.length === 1) {
        // Single match - add directly to cart
        await addProductToCart(matches[0]);
        playBeep(true);
      } else if (matches.length > 1) {
        // Multiple matches - switch to search view. flush so the scanned code
        // filters immediately rather than after the debounce delay.
        setSearchQuery(code);
        flushSearch();
        setViewMode('search');
        playBeep(true);
      } else {
        // No matches at all
        playBeep(false);
        setError(`Not found: "${code}"`);
        setTimeout(() => setError(''), 3000);
      }
    } catch (err: any) {
      // Unexpected error
      playBeep(false);
      setError(`Error: ${err.message || 'Unknown error'}`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setBarcodeInput('');
      setLoading(false);
      // Don't auto-focus - prevents keyboard popup on touchscreens
    }
  };

  const addProductToCart = async (product: POSProduct) => {
    if (!transaction) {
      await handleNewTransaction();
    }

    // weight_tracked (pool of pounds): prompt for the weight printed on the
    // package tag. Fixed price, one line per package, NOT is_weight_based, NO
    // bulk bin. Separate early-return so the existing add-to-cart flow below is
    // untouched for every other product (weight_tracked is false for all of them).
    if (product.weight_tracked) {
      setTagWeightProduct(product);
      setTagWeightValue('');
      setShowTagWeightModal(true);
      return;
    }

    // Determine if this product needs weight/price entry
    const isVariablePrice = product.is_variable_price ||
        (parseFloat(product.effective_price || '0') === 0 && parseFloat(product.retail_price) === 0);
    const needsWeightEntry = product.is_byoc || isVariablePrice;

    // If BYOC or variable price, show weight/price entry modal
    if (needsWeightEntry) {
      setWeightProduct(product);
      setWeightValue('');
      // Pre-populate price if product has one (for BYOC), leave empty for variable price
      setPriceValue(isVariablePrice && parseFloat(product.effective_price || '0') === 0 ? '' : (product.effective_price || product.retail_price));
      setShowWeightModal(true);
      return;
    }

    const txnId = transaction?.id;
    if (!txnId) return;
    try {
      setLoading(true);
      const response = await posAPI.addItem(txnId, product.id);
      setTransaction(response.transaction);
      showBagCreditNotice(response.bag_credit_notice);
      playBeep(true);
    } catch (err: any) {
      // Add-to-cart guard: block unavailable items BEFORE they enter the cart.
      // 'oversell' (incl. no-row → 0 available) routes to inline count-in;
      // 'reserved' to deliberate override. Retry re-adds after the fix.
      if (handleGuardError(err, async (ov) => {
        const r = await posAPI.addItem(txnId, product.id, 1, false, undefined, undefined, ov);
        setTransaction(r.transaction);
        showBagCreditNotice(r.bag_credit_notice);
        playBeep(true);
      })) {
        playBeep(false);
        return;
      }
      setError(err.message || 'Failed to add item');
      playBeep(false);
    } finally {
      setLoading(false);
    }
  };

  const handleWeightSubmit = async () => {
    if (!weightProduct || !transaction) return;

    const weight = parseFloat(weightValue);
    if (isNaN(weight) || weight <= 0) {
      setError('Please enter a valid weight');
      return;
    }

    // For variable price products, validate price entry
    const isVariablePrice = weightProduct.is_variable_price ||
        (parseFloat(weightProduct.effective_price || '0') === 0 && parseFloat(weightProduct.retail_price) === 0);
    const price = parseFloat(priceValue);
    if (isVariablePrice && (isNaN(price) || price <= 0)) {
      setError('Please enter a valid price');
      return;
    }

    // Pass custom price for variable price products (hoisted so the guard
    // retry closure in catch can reuse it).
    const customPrice = isVariablePrice ? price : undefined;
    try {
      setLoading(true);
      const response = await posAPI.addItem(transaction.id, weightProduct.id, weight, true, undefined, customPrice);
      setTransaction(response.transaction);
      setShowWeightModal(false);
      setWeightProduct(null);
      setWeightValue('');
      setPriceValue('');
      playBeep(true);
    } catch (err: any) {
      if (handleGuardError(err, async (ov) => {
        const r = await posAPI.addItem(transaction.id, weightProduct.id, weight, true, undefined, customPrice, ov);
        setTransaction(r.transaction);
        setShowWeightModal(false);
        setWeightProduct(null);
        setWeightValue('');
        setPriceValue('');
        playBeep(true);
      })) {
        playBeep(false);
        return;
      }
      setError(err.message || 'Failed to add item');
      playBeep(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTagWeightSubmit = async () => {
    if (!tagWeightProduct || !transaction) return;

    const weight = parseFloat(tagWeightValue);
    if (isNaN(weight) || weight <= 0) {
      // Blank/invalid: do NOT add, never default a weight.
      setError('Enter the weight printed on the package tag');
      return;
    }
    try {
      setLoading(true);
      // quantity=1, is_weight_based=false, no bin, no custom price (fixed retail);
      // the typed tag weight rides on sold_weight_lbs (last arg). weight_tracked is
      // warn-and-allow on the pool, so no oversell guard/retry is needed.
      const response = await posAPI.addItem(
        transaction.id, tagWeightProduct.id, 1, false, undefined, undefined, undefined, weight);
      setTransaction(response.transaction);
      setShowTagWeightModal(false);
      setTagWeightProduct(null);
      setTagWeightValue('');
      playBeep(true);
    } catch (err: any) {
      setError(err.message || 'Failed to add item');
      playBeep(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
    if (!transaction) return;

    try {
      setLoading(true);
      if (newQuantity <= 0) {
        // Use removeItem and get full updated transaction from backend
        const response = await posAPI.removeItem(transaction.id, itemId);
        setTransaction(response.transaction);
      } else {
        // Use updateItem and get full updated transaction with recalculated totals
        const response = await posAPI.updateItem(transaction.id, itemId, newQuantity);
        setTransaction(response.transaction);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    if (!transaction) return;

    try {
      setLoading(true);
      const response = await posAPI.removeItem(transaction.id, itemId);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to remove item');
    } finally {
      setLoading(false);
    }
  };

  const handleVoidTransaction = async () => {
    if (!transaction) return;

    if (!window.confirm('Are you sure you want to void this transaction?')) return;

    try {
      setLoading(true);
      await posAPI.voidTransaction(transaction.id, 'Voided by cashier');
      setTransaction(null);
      setRedeemMessage(null);  // reward was reversed server-side — drop the banner
      setViewMode('checkout');
    } catch (err: any) {
      setError(err.message || 'Failed to void transaction');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // DISCOUNT HANDLERS
  // ============================================

  const openItemDiscount = (itemId: number) => {
    setDiscountTarget('item');
    setDiscountItemId(itemId);
    setDiscountType('percent');
    setDiscountValue('');
    setShowDiscountModal(true);
  };

  const openCartDiscount = () => {
    setDiscountTarget('cart');
    setDiscountItemId(null);
    setDiscountType('percent');
    setDiscountValue('');
    setShowDiscountModal(true);
  };

  const handleApplyDiscount = async () => {
    if (!transaction || !discountValue) return;
    const val = parseFloat(discountValue);
    if (isNaN(val) || val <= 0) return;

    try {
      setLoading(true);
      let response;
      if (discountTarget === 'item' && discountItemId) {
        response = await posAPI.applyItemDiscount(transaction.id, discountItemId, discountType, val);
      } else {
        response = await posAPI.applyCartDiscount(transaction.id, discountType, val);
      }
      setTransaction(response.transaction);
      setShowDiscountModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to apply discount');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItemDiscount = async (itemId: number) => {
    if (!transaction) return;
    try {
      setLoading(true);
      const response = await posAPI.removeItemDiscount(transaction.id, itemId);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to remove discount');
    } finally {
      setLoading(false);
    }
  };

  // ── Persistent cart + parked carts (per-cashier) ────────────────────────
  const ACTIVE_TXN_KEY = 'pos_active_txn';

  // Remember the active cart across navigation: persist its number so returning
  // to POS restores it instead of minting a fresh empty cart (see loadSession).
  useEffect(() => {
    if (transaction && transaction.status === 'in_progress' && !transaction.parked_at) {
      sessionStorage.setItem(ACTIVE_TXN_KEY, transaction.transaction_number);
    }
  }, [transaction]);

  const refreshParkedCarts = useCallback(async () => {
    try {
      const res = await posAPI.getParkedCarts();
      setParkedCarts(res.parked_carts || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { if (session) refreshParkedCarts(); }, [session, refreshParkedCarts]);

  // Set the active cart to a fresh empty transaction and remember it.
  const startFreshActiveCart = async () => {
    const fresh = await posAPI.createTransaction();
    setTransaction(fresh.transaction);
    sessionStorage.setItem(ACTIVE_TXN_KEY, fresh.transaction.transaction_number);
    return fresh.transaction;
  };

  // Set the current active cart aside (park if it has items, discard if empty).
  const setCurrentAside = async () => {
    if (!transaction) return;
    if (transaction.items.length > 0) {
      await posAPI.parkCart(transaction.id);
    } else {
      await posAPI.discardParkedCart(transaction.id).catch(() => { /* empty cart */ });
    }
  };

  const handleParkSale = async () => {
    if (!transaction) return;
    try {
      setLoading(true);
      await setCurrentAside();
      await startFreshActiveCart();
      await refreshParkedCarts();
    } catch (err: any) {
      setError(err.message || 'Failed to park sale');
    } finally { setLoading(false); }
  };

  const handleRecallCart = async (id: number) => {
    try {
      setLoading(true);
      await setCurrentAside();              // park/discard the current active first
      const res = await posAPI.recallCart(id);   // re-derived server-side on recall
      setTransaction(res.transaction);
      sessionStorage.setItem(ACTIVE_TXN_KEY, res.transaction.transaction_number);
      await refreshParkedCarts();
    } catch (err: any) {
      setError(err.message || 'Failed to recall cart');
    } finally { setLoading(false); }
  };

  const handleDiscardCart = async (id: number) => {
    try {
      setLoading(true);
      await posAPI.discardParkedCart(id);
      await refreshParkedCarts();
    } catch (err: any) {
      setError(err.message || 'Failed to discard cart');
    } finally { setLoading(false); }
  };

  // Loud cashier alert when bag credit auto-applies (never silent — Brad's
  // ruling). Built from the backend's bag_credit_notice; also surfaces the
  // partial split ("2 applied from credit, 1 sold").
  const showBagCreditNotice = (
    notices?: Array<{ product: string; pulled: number; sold: number; remaining_after: number; granted: number }>,
  ) => {
    if (!notices || notices.length === 0) return;
    const msg = notices.map((n) => {
      const parts = [`${n.pulled} applied from credit`];
      if (n.sold > 0) parts.push(`${n.sold} sold`);
      return `${n.product} — ${parts.join(', ')} (${n.remaining_after} of ${n.granted} remaining)`;
    }).join('  •  ');
    setBagCreditNotice(msg);
  };

  // Pay down the customer's tab: add a pay-down line (collected by real tender).
  const handlePayDownTab = async () => {
    if (!transaction?.tab) return;
    const owed = parseFloat(transaction.tab.balance);
    const entry = window.prompt(`Pay toward tab (balance $${owed.toFixed(2)}). Amount:`);
    if (entry === null) return;
    const amount = parseFloat(entry);
    if (isNaN(amount) || amount <= 0) { setError('Enter a valid amount'); return; }
    try {
      setLoading(true);
      const response = await posAPI.addTabPayment(transaction.id, amount);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to add tab payment');
    } finally {
      setLoading(false);
    }
  };

  // Show the receipt for a just-completed sale (mirrors the cash/check success tail).
  const showCompletedReceipt = async (txnId: number) => {
    const binResults = await loadSelectedBins(txnId);
    setBinLoadResults(binResults);
    const receiptData = await posAPI.getReceiptData(txnId);
    setReceipt(receiptData);
    setViewMode('receipt');
  };

  // Charge the cart's remaining balance to the customer's tab. On an over-limit
  // 409 the cashier is prompted for a manager PIN to authorize (or a manager-role
  // user needs none); the override is logged server-side.
  const handleChargeToTab = async () => {
    if (!transaction) return;
    const txnId = transaction.id;
    const attempt = (managerPin?: string) => posAPI.processTabCharge(txnId, undefined, managerPin);
    try {
      setLoading(true);
      let response;
      try {
        response = await attempt();
      } catch (err: any) {
        if (!(err?.data?.requires_manager_override || err?.requires_manager_override)) throw err;
        const reason = err?.data?.error || err?.message || 'Over tab limit.';
        const pin = window.prompt(`${reason}\n\nManager PIN to override (Cancel to abort):`);
        if (!pin) { setError('Charge to tab cancelled — over limit.'); return; }
        response = await attempt(pin);
      }
      setTransaction(response.transaction);
      if (response.transaction?.status === 'completed') {
        await showCompletedReceipt(txnId);
      }
    } catch (err: any) {
      setError(err?.data?.error || err?.message || 'Failed to charge to tab');
    } finally {
      setLoading(false);
    }
  };

  // Decline a bag-credit auto-pull on a line: ring it as a normal sale instead
  // of pulling from the member's grant.
  const handleDeclineBagPull = async (itemId: number) => {
    if (!transaction) return;
    try {
      setLoading(true);
      const response = await posAPI.declineBagPull(transaction.id, itemId);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to decline bag credit');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCartDiscount = async () => {
    if (!transaction) return;
    try {
      setLoading(true);
      const response = await posAPI.removeCartDiscount(transaction.id);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to remove discount');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // TAX EXEMPT HANDLER
  // ============================================

  const handleToggleTaxExempt = async () => {
    if (!transaction) return;
    try {
      setLoading(true);
      setError('');
      const response = await posAPI.toggleTaxExempt(transaction.id);
      setTransaction(response.transaction);
      // Tell the cashier plainly what happened — never a silent no-op.
      const s = response.exemption_summary;
      if (s && s.tax_exempt) {
        if (s.exempted_count === 0) {
          setError(
            `Tax exempt ON, but 0 of ${s.product_line_count} item(s) are exemption-eligible — ` +
            `no tax was removed. Only feed products flagged eligible can be exempted.`
          );
        } else if (s.not_eligible_count > 0) {
          setError(
            `${s.exempted_count} of ${s.product_line_count} item(s) exempted. ` +
            `${s.not_eligible_count} not exemption-eligible (still taxed): ${s.not_eligible_items.join(', ')}.`
          );
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle tax exempt');
    } finally {
      setLoading(false);
    }
  };

  // Per-line cashier exemption control (Build B). Decoupled from product
  // eligibility — a cashier with a valid cert may exempt a non-eligible line,
  // or un-exempt an eligible one. The whole-cart toggle still pre-selects.
  const handleSetLineTaxExempt = async (itemId: number, next: boolean) => {
    if (!transaction) return;
    try {
      setLoading(true);
      setError('');
      const response = await posAPI.setLineTaxExempt(transaction.id, itemId, next);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to change line tax exemption');
    } finally {
      setLoading(false);
    }
  };

  // Product Search - filter locally from allProducts for instant results
  const handleSearch = useCallback((query: string, categoryId?: number) => {
    const searchTerm = query.toLowerCase().trim();

    // Start with all products
    let results = [...allProducts];

    // Filter by category if specified
    if (categoryId) {
      results = results.filter(p => p.product_category === categoryId);
    }

    // Filter by search term (searches name, sku, barcode)
    if (searchTerm) {
      results = results.filter(p =>
        p.name.toLowerCase().includes(searchTerm) ||
        (p.sku && p.sku.toLowerCase().includes(searchTerm)) ||
        (p.barcode && p.barcode.toLowerCase().includes(searchTerm))
      );
    }

    // Sort results: exact matches first, then by name
    results.sort((a, b) => {
      const aExact = a.name.toLowerCase().startsWith(searchTerm);
      const bExact = b.name.toLowerCase().startsWith(searchTerm);
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return a.name.localeCompare(b.name);
    });

    setSearchResults(results);
  }, [allProducts]);

  useEffect(() => {
    if (viewMode === 'search') {
      // Filter on the DEBOUNCED value — the expensive filter/sort of allProducts runs
      // once the user pauses, not on every keystroke.
      handleSearch(debouncedQuery, selectedCategory || undefined);
    }
  }, [viewMode, debouncedQuery, selectedCategory, handleSearch]);

  // Payment handlers
  // Prepare bin load selections — ONE row per feed cart line, one bin each. The bin is
  // defaulted only when unambiguous (single candidate, or a single bin); otherwise the
  // cashier must choose. Weight is the product's real size (quantity x size_lbs, BYOC=1)
  // or the weighed sold_weight_lbs — never quantity x 40. See binLoadSelections.ts.
  const prepareBinLoadSelections = () => {
    if (!selectedCustomer || customerBins.length === 0 || !transaction?.items) {
      setBinLoadSelections([]);
      return;
    }
    const productsById: Record<number, { id: number; size_lbs?: string | number | null }> =
      Object.fromEntries(allProducts.map(p => [p.id, p]));
    const bins = customerBins.map(b => ({
      id: b.id,
      name: b.name,
      feed_category: b.feed_category,
      feed_category_display: b.feed_category_display,
    }));
    const lines = transaction.items.map((it: any) => ({
      product: it.product ?? null,
      product_name: it.product_name || '',
      quantity: it.quantity,
      sold_weight_lbs: it.sold_weight_lbs ?? null,
      is_weight_based: it.is_weight_based,
    }));
    setBinLoadSelections(buildBinLoadSelections(lines, bins, productsById));
  };

  // Auto-prepare bin selections when customer or cart changes
  useEffect(() => {
    console.log('[POS] useEffect: customerBins changed, length:', customerBins.length);
    if (selectedCustomer && customerBins.length > 0) {
      prepareBinLoadSelections();
    }
  }, [customerBins, selectedCustomer, transaction?.items]);

  // True when the transaction has no Bridge Club member AND no customer
  // of any kind attached — i.e. an unattributed walk-in. selectedCustomer
  // covers all three attach paths (BC member, walk-in visitor, wholesale);
  // the transaction.* fields are the persisted belt-and-suspenders.
  const isUnattributedWalkin = (): boolean => {
    if (!transaction) return false;
    if (selectedCustomer) return false;
    if (transaction.bridge_club_member) return false;
    if (transaction.attached_customer_id || transaction.customer) return false;
    // Refund/void txns never reach handlePayment, but guard defensively.
    if (viewMode === 'refund') return false;
    return true;
  };

  // The actual cart → payment transition (no nudge gate).
  const proceedToPayment = () => {
    prepareBinLoadSelections();
    setViewMode('payment');
    setCashTendered('');
    setChangeDue(null);
    setPaymentMethod('cash');
    setBinLoadResults([]);
  };

  const handlePayment = () => {
    if (!transaction || transaction.items.length === 0) {
      setError('No items in cart');
      return;
    }
    // Workflow nudge: stop missed BC signups at the till. On an
    // unattributed walk-in, intercept Pay with the prompt instead of
    // going straight to payment.
    if (isUnattributedWalkin()) {
      setShowBcPrompt(true);
      return;
    }
    proceedToPayment();
  };

  // BC prompt — "Look Up / Add Customer": close the nudge and open the
  // EXISTING customer lookup/add modal. After the cashier attaches a
  // customer there, they land back in the cart and click Pay again — this
  // time isUnattributedWalkin() is false, so payment proceeds.
  const handleBcPromptLookup = () => {
    setShowBcPrompt(false);
    setShowCustomerModal(true);
  };

  // BC prompt — "Skip — Just a Walk-in": log the skip (analytics), then
  // proceed to payment. Skip-logging is best-effort; never blocks payment.
  const handleBcPromptSkip = async () => {
    setShowBcPrompt(false);
    if (transaction) {
      try {
        await posAPI.logBcSignupSkip(transaction.id);
      } catch (err) {
        console.error('Failed to log BC signup skip:', err);
      }
    }
    proceedToPayment();
  };

  // BC prompt — "Cancel — Back to Cart": no payment, nothing logged.
  const handleBcPromptCancel = () => {
    setShowBcPrompt(false);
  };

  // Load bins after payment - returns array of load results for receipt
  const loadSelectedBins = async (transactionId: number): Promise<Array<{binName: string; addedLbs: number; newLevel: number; fillPct: number}>> => {
    if (!selectedCustomer || selectedCustomer.id == null || binLoadSelections.length === 0) {
      return [];
    }
    const memberId: number = selectedCustomer.id;

    const results: Array<{binName: string; addedLbs: number; newLevel: number; fillPct: number}> = [];

    for (const selection of binLoadSelections) {
      if (selection.binId == null || selection.quantity <= 0) continue;

      try {
        const response = await posAPI.loadBin(memberId, selection.binId, {
          product_id: selection.productId || undefined,
          quantity_lbs: selection.quantity,   // server recomputes authoritatively (load_bin)
          pos_transaction_id: transactionId,
          product_name: selection.productName || '',
        });

        results.push({
          binName: response.bin.name,
          addedLbs: response.bin.added_lbs,
          newLevel: response.bin.current_lbs,
          fillPct: response.bin.fill_percentage,
        });
      } catch (err) {
        console.error(`Failed to load bin ${selection.binId}:`, err);
      }
    }

    return results;
  };

  // Intercept a 409 reservation/oversell block. Returns true if it handled the
  // error (caller should stop), false to fall through to normal error display.
  const handleGuardError = (
    err: any,
    retry: (allowOverride: boolean) => Promise<void>,
  ): boolean => {
    const data = err?.data;
    if (err?.status === 409 && data?.requires_override) {
      const requested = data.requested ?? 0;
      const available = data.available ?? 0;
      setGuardBlock({
        code: data.code,
        product: data.product || 'this item',
        productId: data.product_id ?? null,
        requested,
        available,
        retry,
      });
      // Default the count-in to the shortfall; cashier can edit it up.
      if (data.code === 'oversell') {
        setGuardAddQty(String(Math.max(1, requested - available)));
      }
      return true;
    }
    return false;
  };

  // 'oversell' path: count in uncounted stock, then retry the sale (no override
  // — the stock genuinely exists now, so the guard clears normally).
  const handleGuardCountIn = async () => {
    if (!guardBlock || guardBlock.productId == null) return;
    const qty = parseInt(guardAddQty, 10);
    if (isNaN(qty) || qty <= 0) {
      setError('Enter a quantity to add');
      return;
    }
    const block = guardBlock;
    const productId = guardBlock.productId;  // narrowed to number by the guard above
    try {
      setGuardBusy(true);
      await posAPI.quickAddInventory(productId, qty, 'POS inline count-in');
      setGuardBlock(null);
      setGuardAddQty('');
      await block.retry(false);
    } catch (err: any) {
      setError(err.message || 'Could not add inventory');
    } finally {
      setGuardBusy(false);
    }
  };

  // 'reserved' path: deliberate manager override — retry the sale forcing
  // through reserved stock (backend flags the mill's pull SHORT).
  const handleGuardOverride = async () => {
    if (!guardBlock) return;
    const block = guardBlock;
    try {
      setGuardBusy(true);
      setGuardBlock(null);
      await block.retry(true);
    } catch (err: any) {
      setError(err.message || 'Override failed');
    } finally {
      setGuardBusy(false);
    }
  };

  const handleCashPayment = async (allowOverride: boolean = false) => {
    if (!transaction) return;

    const tendered = parseFloat(cashTendered);
    if (isNaN(tendered) || tendered <= 0) {
      setError('Enter cash amount');
      return;
    }

    // Split-payment path: explicit slice of remaining balance. Tendered must
    // cover the slice (change_given is computed on the server).
    // Single-tender path (unchanged): tendered must cover the full total.
    let splitAmount: number | undefined;
    if (splitMode) {
      const remaining = amountDue;
      // Default slice = min(tendered, remaining) so a $40 bill on a $30
      // balance pays it down and gives $10 change without the cashier
      // typing the slice explicitly.
      splitAmount = splitAmountInput
        ? parseFloat(splitAmountInput)
        : Math.min(tendered, remaining);
      if (isNaN(splitAmount) || splitAmount <= 0 || splitAmount > remaining + 0.001) {
        setError(`Split amount must be between $0.01 and $${remaining.toFixed(2)}`);
        return;
      }
      if (tendered < splitAmount) {
        setError('Cash tendered is less than the split amount');
        return;
      }
    } else {
      const total = parseFloat(transaction.total);
      if (tendered < total) {
        setError('Insufficient payment');
        return;
      }
    }

    try {
      setLoading(true);
      const response = await posAPI.processCashPayment(transaction.id, tendered, splitAmount, allowOverride);
      setChangeDue(response.change_due);
      setTransaction(response.transaction);
      setCashTendered('');
      setSplitAmountInput('');

      // In split mode, if the transaction is not yet fully paid, stay on
      // the payment screen and refresh the remaining balance.
      if (splitMode && response.transaction.status !== 'completed') {
        await refreshAmountDue(transaction.id);
        setError('');
        return;
      }

      // Load bins if customer has any selected
      const binResults = await loadSelectedBins(transaction.id);
      setBinLoadResults(binResults);

      // Load receipt and show it
      const receiptData = await posAPI.getReceiptData(transaction.id);
      setReceipt(receiptData);
      setViewMode('receipt');
    } catch (err: any) {
      if (handleGuardError(err, (ov) => handleCashPayment(ov))) return;
      setError(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckPayment = async (allowOverride: boolean = false) => {
    if (!transaction) return;

    if (!checkNumber.trim()) {
      setError('Please enter a check number');
      return;
    }

    // Split-payment path: explicit slice of remaining balance.
    let splitAmount: number | undefined;
    if (splitMode) {
      const remaining = amountDue;
      splitAmount = splitAmountInput ? parseFloat(splitAmountInput) : remaining;
      if (isNaN(splitAmount) || splitAmount <= 0 || splitAmount > remaining + 0.001) {
        setError(`Split amount must be between $0.01 and $${remaining.toFixed(2)}`);
        return;
      }
    }

    try {
      setLoading(true);
      const response = await posAPI.processCheckPayment(transaction.id, checkNumber.trim(), splitAmount, allowOverride);
      setTransaction(response.transaction);
      setCheckNumber('');
      setSplitAmountInput('');

      // In split mode, if not yet fully paid, refresh and stay on payment screen.
      if (splitMode && response.transaction.status !== 'completed') {
        await refreshAmountDue(transaction.id);
        setError('');
        return;
      }

      // Load bins if customer has any selected
      const binResults = await loadSelectedBins(transaction.id);
      setBinLoadResults(binResults);

      // Load receipt and show it
      const receiptData = await posAPI.getReceiptData(transaction.id);
      setReceipt(receiptData);
      setViewMode('receipt');
    } catch (err: any) {
      if (handleGuardError(err, (ov) => handleCheckPayment(ov))) return;
      setError(err.message || 'Check payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async (allowOverride: boolean = false) => {
    if (!transaction) return;

    // Split-payment path: charge a slice of amount_due. Default (single-tender)
    // path charges the entire remaining balance — backend behavior unchanged.
    let chargeAmount: number | undefined;
    if (splitMode) {
      const remaining = amountDue;
      chargeAmount = splitAmountInput ? parseFloat(splitAmountInput) : remaining;
      if (isNaN(chargeAmount) || chargeAmount <= 0 || chargeAmount > remaining + 0.001) {
        setError(`Split amount must be between $0.01 and $${remaining.toFixed(2)}`);
        return;
      }
    }

    try {
      setLoading(true);
      setCardPaymentStatus('Waiting for card...');
      setCardPaymentApproved(false);
      setCardIsCredit(null);
      // Start the polling-budget timer. The 90s cap protects against the
      // "customer walked away from terminal" scenario that previously
      // polled forever.
      pollStartTimeRef.current = Date.now();
      const response = await posAPI.initiateCardPayment(transaction.id, chargeAmount, allowOverride);
      setCardPaymentId(response.payment_id);
      setSplitAmountInput('');

      // Poll for payment status
      pollCardPayment(response.payment_id);
    } catch (err: any) {
      if (handleGuardError(err, (ov) => handleCardPayment(ov))) {
        setCardPaymentStatus('');
        pollStartTimeRef.current = null;
        setLoading(false);
        return;
      }
      setError(err.message || 'Failed to initiate card payment');
      setCardPaymentStatus('');
      pollStartTimeRef.current = null;
      setLoading(false);
    }
  };

  const pollCardPayment = async (paymentId: number) => {
    // Polling-budget guard — protects against a terminal that never returns
    // a verdict (customer walked away, terminal lost network). If the
    // budget is blown we surface a Declined card with the timeout reason
    // so the cashier can retry or switch methods.
    if (
      pollStartTimeRef.current !== null &&
      Date.now() - pollStartTimeRef.current > MAX_CARD_POLL_MS
    ) {
      pollStartTimeRef.current = null;
      setCardPaymentStatus('declined');
      setCardPaymentApproved(false);
      setLastDeclineReason(
        'Payment timed out — please check the card reader and try again'
      );
      setCardDeclineCount(prev => prev + 1);
      setLoading(false);
      return;
    }

    try {
      const response = await posAPI.checkCardPayment(paymentId);

      if (response.status === 'approved') {
        pollStartTimeRef.current = null;
        setCardPaymentStatus('Payment approved!');
        setCardPaymentApproved(true);
        setCardIsCredit(response.is_credit ?? null);
        setLoading(false);

        // Update transaction from response
        if (response.transaction) {
          setTransaction(response.transaction);
        }

        const isCompleted = response.transaction?.status === 'completed';

        // In split mode and not yet fully paid, refresh remaining balance
        // and return to the payment screen instead of the receipt.
        if (splitMode && !isCompleted) {
          if (transaction) {
            await refreshAmountDue(transaction.id);
          }
          setCardPaymentStatus('');
          setCardPaymentApproved(false);
          setCardPaymentId(null);
          setCardIsCredit(null);
          return;
        }

        // Try to load bins and receipt, then transition to receipt view
        try {
          const txnId = response.transaction?.id || transaction?.id;
          if (txnId) {
            const binResults = await loadSelectedBins(txnId);
            setBinLoadResults(binResults);

            const receiptData = await posAPI.getReceiptData(txnId);
            setReceipt(receiptData);
            setViewMode('receipt');
            // Clear card payment state after successful transition
            setCardPaymentStatus('');
            setCardPaymentApproved(false);
            setCardPaymentId(null);
            setCardIsCredit(null);
          }
        } catch (receiptErr) {
          // Payment succeeded but receipt/bins failed — stay on approved screen
          // User can click NEW SALE to continue
          console.warn('Receipt loading failed after approved payment:', receiptErr);
        }
      } else if (response.status === 'pending') {
        setCardPaymentStatus(response.message || 'Waiting for card...');
        // Continue polling
        setTimeout(() => pollCardPayment(paymentId), 2000);
      } else {
        // Declined / error / cancelled — show the declined panel with the
        // retry + switch-to-cash actions instead of bouncing back to the
        // initial "ACTIVATE TERMINAL" button. Increments the per-transaction
        // decline counter so the UI can warn after repeated declines.
        pollStartTimeRef.current = null;
        setCardPaymentStatus('declined');
        setCardPaymentApproved(false);
        setLastDeclineReason(response.message || 'Payment declined');
        setCardDeclineCount(prev => prev + 1);
        setLoading(false);
      }
    } catch (err: any) {
      pollStartTimeRef.current = null;
      setCardPaymentStatus('declined');
      setCardPaymentApproved(false);
      setLastDeclineReason(err.message || 'Payment check failed');
      setCardDeclineCount(prev => prev + 1);
      setLoading(false);
    }
  };

  const handleCancelCardPayment = async () => {
    if (!cardPaymentId) return;

    try {
      await posAPI.cancelCardPayment(cardPaymentId);
    } catch (err) {
      // 404 means payment already approved/completed — that's OK
      console.warn('Cancel payment failed (may already be processed):', err);
    }
    // Always clear UI state regardless of backend result
    setCardPaymentId(null);
    setCardPaymentStatus('');
    setCardPaymentApproved(false);
    setCardIsCredit(null);
    setLoading(false);
  };

  const handleNewSale = () => {
    setTransaction(null);
    sessionStorage.removeItem('pos_active_txn');  // the completed cart is done
    setReceipt(null);
    setChangeDue(null);
    setRedeemMessage(null);  // fresh cart — clear any prior reward banner
    setBagCreditNotice(null);  // clear the bag-credit alert for the next sale
    setSelectedCustomer(null);
    setCustomerBins([]);
    setBinLoadSelections([]);
    setBinLoadResults([]);
    // Clear card payment state
    setCardPaymentId(null);
    setCardPaymentStatus('');
    setCardPaymentApproved(false);
    setCardIsCredit(null);
    setCashTendered('');
    // Clear decline + split-payment state
    setCardDeclineCount(0);
    setLastDeclineReason('');
    pollStartTimeRef.current = null;
    setSplitMode(false);
    setSplitPayments([]);
    setSplitAmountInput('');
    setAmountDue(0);
    setViewMode('checkout');
    handleNewTransaction();
  };

  // Keep amountDue synced to the transaction total whenever the cart changes
  // (e.g. add/remove item) so the split-payment banner stays accurate before
  // the first tender. Server is the source of truth — splitMode refresh
  // calls overwrite this after each payment.
  useEffect(() => {
    if (transaction?.total !== undefined) {
      const total = parseFloat(transaction.total);
      // Subtract any already-recorded split payments; the server endpoint
      // does this authoritatively but this keeps the UI honest between
      // refreshes.
      const splitPaid = splitPayments.reduce((sum, p) => sum + p.amount, 0);
      setAmountDue(Math.max(0, total - splitPaid));
    }
  }, [transaction?.total, splitPayments]);

  // Server-truth refresh of amount_due + payments list — called after every
  // successful tender in split mode to drive the remaining-balance banner.
  const refreshAmountDue = useCallback(async (txnId: number) => {
    try {
      const data: any = await posAPI.getTransactionAmountDue(txnId);
      setAmountDue(parseFloat(data.amount_due));
      setSplitPayments((data.payments || []).map((p: any) => ({
        id: p.id,
        method: p.method,
        methodDisplay: p.method_display,
        amount: parseFloat(p.amount),
        cardLastFour: p.card_last_four,
        checkNumber: p.check_number,
        healedFromEmail: !!p.healed_from_email,
      })));
      return data;
    } catch (err) {
      console.warn('Failed to refresh amount due:', err);
      return null;
    }
  }, []);

  // Reset invite UI when the selected customer changes (covers
  // attach/detach and modal cancel paths).
  useEffect(() => {
    setShowInviteInput(false);
    setInviteEmail('');
    setInviteSent(false);
    setInviteSubmitting(false);
  }, [selectedCustomer?.unmatched_visitor_id, selectedCustomer?.id]);

  // Apply one $10 reward (500 pts) for the selected local member.
  // Hits the redeem endpoint with the in-progress transaction id so
  // the credit gets stamped onto the POSTransaction (subtracts from
  // points-eligible base on the post-payment award flow).
  const handleApplyLocalReward = async () => {
    const memberId = selectedCustomer?.id;
    if (!memberId || !selectedCustomer?.local_points || selectedCustomer.local_points.redeemable_rewards <= 0) return;
    setRedeemingPoints(true);
    setRedeemMessage(null);
    try {
      const result = await posAPI.redeemLocalPoints(memberId, 500, transaction?.id) as any;
      setRedeemMessage(result?.message || '$10 credit applied');
      // Refresh the cart from the returned transaction so the credit-reduced
      // total + the "Reward credit" line show immediately. Without this the
      // displayed TOTAL / PAY amount stay at the pre-redeem (full) price even
      // though the backend already reduced txn.total. The amountDue effect
      // re-derives off transaction.total automatically.
      if (result?.transaction) {
        setTransaction(result.transaction);
      }
      // Refresh customer detail so the displayed balance updates.
      try {
        const detail = await posAPI.getCustomerDetail(memberId);
        setSelectedCustomer(detail.member);
      } catch {
        // detail-refresh failure is non-fatal — the redemption already happened
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to apply reward');
    } finally {
      setRedeemingPoints(false);
    }
  };

  // Send a Bridge Club invite to the selected UnmatchedVisitor. POSTs
  // to the staff-only endpoint which creates a BridgeClubMember row
  // and emails them the verification link.
  const handleSendInvite = async () => {
    if (!selectedCustomer?.unmatched_visitor_id || !inviteEmail) return;
    setInviteSubmitting(true);
    try {
      await posAPI.inviteToBridgeClub({
        unmatched_visitor_id: selectedCustomer.unmatched_visitor_id,
        email: inviteEmail.trim(),
      });
      setInviteSent(true);
      setShowInviteInput(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to send invite. Please try again.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  // Customer search handler
  const handleCustomerSearch = async (query: string) => {
    setCustomerSearchQuery(query);
    if (query.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setCustomerSearching(true);
      console.log('[POS] Searching customers with query:', query);
      const response = await posAPI.searchCustomers(query);
      console.log('[POS] Customer search response:', response);
      setCustomerSearchResults(response.members || []);
    } catch (err) {
      console.error('[POS] Customer search failed:', err);
      setCustomerSearchResults([]);
    } finally {
      setCustomerSearching(false);
    }
  };

  // Select customer (for barcode scanning) - loads their bins.
  // Barcode lookups always return real BridgeClubMember rows (never an
  // UnmatchedVisitor — those have no barcode), so member.id is always
  // a real number here.
  const handleSelectCustomer = async (member: POSCustomer) => {
    if (member.id == null) return;
    const memberId: number = member.id;
    try {
      // Fetch customer detail with bins
      console.log('[POS] Fetching customer detail for:', memberId, member.name);
      const detailResponse = await posAPI.getCustomerDetail(memberId);
      console.log('[POS] Customer detail response:', detailResponse);
      console.log('[POS] Customer bins returned:', detailResponse.bins);
      setSelectedCustomer(detailResponse.member);
      setCustomerBins(detailResponse.bins || []);

      // If there's a transaction, attach the customer
      if (transaction) {
        const response = await posAPI.attachCustomer(transaction.id, memberId);
        setTransaction(response.transaction);
      }

      setShowCustomerModal(false);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
    } catch (err: any) {
      setError(err.message || 'Failed to load customer');
      playBeep(false);
    }
  };

  // Load a wholesale Will-Call SalesOrder into a fresh POSTransaction.
  // Sibling of handleAttachCustomer for the third tier of the customer
  // search dropdown. Server-side this:
  //   1. Pre-stages a POSTransaction with source_wholesale_order +
  //      attached_customer + all line items at wholesale pricing.
  //   2. Sets SalesOrder.loaded_in_pos_at to prevent double-load.
  //   3. Returns redirect_url with ?txn=YYYYMMDD-NNNN.
  // We navigate; StorePOS's mount handler picks up ?txn= and resumes
  // the pre-staged cart instead of creating a fresh one.
  const handleLoadWholesaleOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await posAPI.loadWholesaleOrderInPos(orderId);
      // Close the modal — navigation will reload the page with the
      // ?txn= query param.
      setShowCustomerModal(false);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      navigate(response.redirect_url);
      // Force StorePOS to re-mount the transaction by navigating to
      // the redirect_url which carries ?txn=.... If the SPA stays on
      // /store/pos the search param change alone won't re-trigger
      // loadSession; we reload explicitly.
      window.location.href = response.redirect_url;
    } catch (err: any) {
      // apiCall attaches the structured body as err.data (+ err.status);
      // keep the legacy err.response reads as a fallback.
      const detail = err?.data || err?.response?.data;
      const errStatus = err?.status || err?.response?.status;
      // 409 = already loaded by another cashier (race). The server
      // returns the existing transaction so we can navigate to it.
      if (errStatus === 409 && detail?.redirect_url) {
        setShowCustomerModal(false);
        window.location.href = detail.redirect_url;
        return;
      }
      // Guard C: the order's invoice already has payments applied —
      // ringing the full order total would double-charge. Surface the
      // message and open the Open Invoices panel so staff collect the
      // BALANCE instead.
      if (detail?.error === 'invoice_has_payments') {
        setShowCustomerModal(false);
        setCustomerSearchQuery('');
        setCustomerSearchResults([]);
        setError(detail.message);
        if (detail.customer_id) {
          setInvoicePanelTarget({
            customerId: detail.customer_id,
            name: detail.customer_name || 'Customer',
          });
        }
        playBeep(false);
        return;
      }
      setError(
        detail?.message
          || detail?.error
          || err.message
          || 'Failed to load wholesale order',
      );
      playBeep(false);
    } finally {
      setLoading(false);
    }
  };

  // ── Invoice collection (A1) ──────────────────────────────────────
  // Apply an invoice-payment line to the cart. Ensures a transaction
  // exists (mirrors handleNewTransaction), then POSTs the invoice +
  // collection amount; the backend enforces 0 < amount <= balance_due
  // and represents the line as a product-less INVPAY item. Money is
  // recorded against the invoice only when the sale completes.
  const handleApplyInvoicePayment = async (invoiceId: number, amount: number, note: string) => {
    let txn = transaction;
    if (!txn) {
      const created = await posAPI.createTransaction();
      txn = created.transaction;
      setTransaction(txn);
    }
    const response = await posAPI.applyInvoicePayment(txn!.id, invoiceId, amount, note);
    setTransaction(response.transaction);
    setInvoicePanelTarget(null);
    setViewMode('checkout');
    playBeep(true);
  };

  const handleRemoveInvoicePayment = async () => {
    if (!transaction) return;
    try {
      const response = await posAPI.removeInvoicePayment(transaction.id);
      setTransaction(response.transaction);
    } catch (err: any) {
      setError(err.message || 'Failed to remove invoice payment');
    }
  };

  // Attach customer to transaction (from customer modal)
  // Open the "Add new Bridge Club customer" inline form from the
  // zero-results affordance. Smart pre-fill: if the search query
  // contains "@" → email; if it's mostly digits → phone; otherwise
  // first-token → first_name and remaining-tokens → last_name.
  const openNewBcForm = () => {
    const q = (customerSearchQuery || '').trim();
    setNewBcError(null);
    setNewBcExistingMember(null);
    setNewBcPhoneCandidates([]);
    setNewBcFirstName('');
    setNewBcLastName('');
    setNewBcEmail('');
    setNewBcPhone('');

    if (!q) {
      setShowNewBcForm(true);
      return;
    }

    const digitsOnly = q.replace(/\D/g, '');
    if (q.includes('@')) {
      setNewBcEmail(q);
    } else if (digitsOnly.length >= 7 && digitsOnly.length === q.replace(/[^\d\s\-().+]/g, '').length) {
      // Mostly digits + phone-formatting chars only.
      setNewBcPhone(q);
    } else {
      const parts = q.split(/\s+/).filter(Boolean);
      if (parts.length >= 1) setNewBcFirstName(parts[0]);
      if (parts.length >= 2) setNewBcLastName(parts.slice(1).join(' '));
    }
    setShowNewBcForm(true);
  };

  const closeNewBcForm = () => {
    setShowNewBcForm(false);
    setNewBcError(null);
    setNewBcExistingMember(null);
    setNewBcPhoneCandidates([]);
    setNewBcSubmitting(false);
  };

  // Submit the new-BC-member form. Bypass the soft phone-match check
  // on retry by passing ignorePhoneMatch=true.
  const submitNewBcForm = async (ignorePhoneMatch: boolean = false) => {
    setNewBcError(null);
    setNewBcExistingMember(null);
    setNewBcPhoneCandidates([]);

    const firstName = newBcFirstName.trim();
    const lastName = newBcLastName.trim();
    const email = newBcEmail.trim().toLowerCase();
    const phone = newBcPhone.trim();

    if (!firstName || !lastName || !email || !phone) {
      setNewBcError('First name, last name, email, and phone are all required.');
      return;
    }

    setNewBcSubmitting(true);
    try {
      const response: any = await posAPI.createBridgeClubMember({
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        attach_to_transaction_id: transaction?.id ?? null,
        ignore_phone_match: ignorePhoneMatch,
      });

      // Phone soft-match path: backend returns 200 with warning='phone_match'.
      // Don't treat as success — surface candidates + Create Anyway button.
      if (response?.warning === 'phone_match') {
        setNewBcPhoneCandidates(response.candidates || []);
        setNewBcError(response.message || 'Phone number already on file.');
        setNewBcSubmitting(false);
        return;
      }

      // Success path: backend created the member AND (optionally)
      // attached it to the in-progress transaction.
      if (response?.member) {
        const newMember: POSCustomer = response.member;
        setSelectedCustomer(newMember);
        setCustomerBins([]);
        if (response.attached_to_transaction_id && transaction) {
          // Mirror attach into local transaction state so the cart
          // header reflects the new customer without a re-fetch.
          setTransaction({
            ...transaction,
            customer: newMember.id,
            customer_name: newMember.name,
          } as any);
        }
        if (!response.email_send_succeeded) {
          // Non-fatal: surface a small warning toast via setError so
          // the cashier knows to resend from the profile.
          setError(
            `Added ${newMember.name} to Bridge Club, but the welcome ` +
            `email failed to send. You can resend it from the customer's profile.`
          );
        }
        playBeep(true);
        closeNewBcForm();
        setShowCustomerModal(false);
        setCustomerSearchQuery('');
        setCustomerSearchResults([]);
      }
    } catch (err: any) {
      const detail = err?.response?.data;
      // 409 duplicate_email: backend returns existing_member as a
      // full POSCustomer shape so we can offer one-click attach.
      if (detail?.error === 'duplicate_email' && detail?.existing_member) {
        setNewBcExistingMember(detail.existing_member);
        setNewBcError(detail.message || 'This email is already a Bridge Club member.');
      } else {
        setNewBcError(
          detail?.message || err?.message || 'Failed to create member.'
        );
      }
    } finally {
      setNewBcSubmitting(false);
    }
  };

  const handleAttachCustomer = async (member: POSCustomer | null) => {
    if (!transaction) return;
    // Any customer change invalidates a prior redemption banner; clearing
    // the customer also reverses the redemption server-side.
    setRedeemMessage(null);

    // Link-mode: a walk-in is being linked to an EXISTING member. Credit
    // the walk-in's spend/points to the chosen member, then fall through
    // to attach that real member to the current sale.
    if (linkWalkinVisitorId != null) {
      if (!member || member.is_unmatched || member.id == null) {
        setError('Pick an existing Bridge Club member to link this walk-in to.');
        return;
      }
      try {
        const res: any = await posAPI.linkWalkinToMember(linkWalkinVisitorId, member.id as number);
        setLinkMessage(res?.message || `Linked walk-in to ${member.name}.`);
        playBeep(true);
      } catch (err: any) {
        setError(err?.message || 'Failed to link walk-in to member.');
        return;
      } finally {
        setLinkWalkinVisitorId(null);
      }
      // fall through → attach the now-linked member to this transaction
    }

    try {
      setLoading(true);

      // UnmatchedVisitor (walk-in shadow profile): no BridgeClubMember
      // row exists yet, so we can't call getCustomerDetail or attach to
      // the transaction. We just stash the visitor on selectedCustomer
      // so the invite banner renders. The till sale stays uncustomered
      // until the invite is accepted (and a BC member is created).
      if (member?.is_unmatched) {
        setSelectedCustomer(member);
        setCustomerBins([]);
        setShowCustomerModal(false);
        setCustomerSearchQuery('');
        setCustomerSearchResults([]);
        playBeep(true);
        return;
      }

      if (member) {
        // Fetch customer detail with bins
        console.log('[POS] Attaching customer, fetching details:', member.id, member.name);
        const detailResponse = await posAPI.getCustomerDetail(member.id as number);
        console.log('[POS] Attach customer detail response:', detailResponse);
        console.log('[POS] Attach customer bins returned:', detailResponse.bins);
        setSelectedCustomer(detailResponse.member);
        setCustomerBins(detailResponse.bins || []);
      } else {
        setSelectedCustomer(null);
        setCustomerBins([]);
      }

      const response = await posAPI.attachCustomer(transaction.id, (member?.id ?? null) as number | null);
      setTransaction(response.transaction);
      showBagCreditNotice(response.bag_credit_notice);
      setShowCustomerModal(false);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      if (member) {
        playBeep(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to attach customer');
      playBeep(false);
    } finally {
      setLoading(false);
    }
  };

  // Load the session summary and open the closeout modal.
  const openCloseoutModal = async () => {
    if (!session) return;
    try {
      setLoading(true);
      const response = await posAPI.getSessionSummary(session.id);
      setSessionSummary(response);
      setClosingCash('');
      setClosingChecks('');
      setClosingNotes('');
      setShowCloseoutModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load session summary');
    } finally {
      setLoading(false);
    }
  };

  // End shift. If the cashier still has parked carts, prompt keep-or-discard
  // first (option C); with none, go straight to the closeout modal.
  const handleOpenCloseout = async () => {
    if (!session) return;
    await refreshParkedCarts();
    const current = await posAPI.getParkedCarts().then(r => r.parked_carts || []).catch(() => parkedCarts);
    if (current.length > 0) {
      setParkedCarts(current);
      setShowParkedPrompt(true);
      return;
    }
    await openCloseoutModal();
  };

  // Prompt: keep the parked carts (stay in_progress → repopulate next login).
  const handleKeepParkedCarts = async () => {
    setShowParkedPrompt(false);
    await openCloseoutModal();
  };

  // Prompt: discard all parked carts, then continue to closeout.
  const handleDiscardAllParkedCarts = async () => {
    try {
      setLoading(true);
      await Promise.all(parkedCarts.map(c => posAPI.discardParkedCart(c.id).catch(() => null)));
      await refreshParkedCarts();
    } catch { /* non-fatal */ } finally {
      setLoading(false);
    }
    setShowParkedPrompt(false);
    await openCloseoutModal();
  };

  // Close shift
  const handleCloseShift = async () => {
    if (!session) return;

    const cash = parseFloat(closingCash);
    if (isNaN(cash)) {
      setError('Please enter a valid closing cash amount');
      return;
    }

    try {
      setLoading(true);
      const sessionId = session.id;
      const checks = closingChecks ? parseFloat(closingChecks) : null;
      await posAPI.closeSession(sessionId, cash, checks, closingNotes);

      // Auto-print till close report (non-blocking)
      try {
        const report = await posAPI.getCloseReport(sessionId);
        await printTillReport(report);
      } catch (printErr) {
        console.warn('Till report print failed (shift still closed):', printErr);
      }

      setShowCloseoutModal(false);
      setSession(null);
      playBeep(true);

      // Auto-logout: lock the terminal until next login
      setError('Register closed. Logging out...');
      setTimeout(async () => {
        await logout();
        navigate('/store/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to close session');
      playBeep(false);
    } finally {
      setLoading(false);
    }
  };

  // Petty cash handlers
  const handleOpenPettyCash = async () => {
    if (!session) return;
    setPettyCashError('');
    setPettyCashAmount('');
    setPettyCashCategory('supplies');
    setPettyCashDescription('');
    setShowPettyCashModal(true);

    try {
      const result = await posAPI.getPettyCashWithdrawals({ session_id: session.id });
      setPettyCashWithdrawals(result.withdrawals || []);
      // Calculate cash in drawer from session summary
      const summary = await posAPI.getSessionSummary(session.id);
      setPettyCashDrawerAmount(summary.expected_cash);
    } catch (err: any) {
      setPettyCashError(err.message || 'Failed to load petty cash data');
    }
  };

  const handlePettyCashWithdraw = async () => {
    if (!session) return;
    const amount = parseFloat(pettyCashAmount);
    if (isNaN(amount) || amount <= 0) {
      setPettyCashError('Enter a valid amount');
      return;
    }
    if (pettyCashCategory === 'other' && !pettyCashDescription.trim()) {
      setPettyCashError('Description is required for "Other" category');
      return;
    }

    try {
      setPettyCashLoading(true);
      setPettyCashError('');
      const result = await posAPI.createPettyCashWithdrawal(session.id, amount, pettyCashCategory, pettyCashDescription);
      setPettyCashDrawerAmount(result.cash_in_drawer);
      setPettyCashWithdrawals(prev => [result.withdrawal, ...prev]);
      setPettyCashAmount('');
      setPettyCashDescription('');
      setPettyCashCategory('supplies');
      setShowPettyCashModal(false);
      playBeep(true);
    } catch (err: any) {
      setPettyCashError(err.message || 'Failed to create withdrawal');
      playBeep(false);
    } finally {
      setPettyCashLoading(false);
    }
  };

  // Refund handlers
  const handleRefundLookup = async () => {
    if (!refundTxnNumber.trim()) return;
    try {
      setRefundLookupLoading(true);
      setRefundLookupError('');
      setRefundLookupData(null);
      setRefundResult(null);
      setRefundReceipt(null);
      setRefundTapMessage('');
      const response = await posAPI.lookupForRefund(refundTxnNumber.trim());
      setRefundLookupData(response);
      setRefundType('full');
      setRefundSelectedItems(new Set());
      setRefundCustomAmount('');
      setRefundReason('');
    } catch (err: any) {
      setRefundLookupError(err.message || 'Transaction not found');
    } finally {
      setRefundLookupLoading(false);
    }
  };

  const handleRefundToggleItem = (itemId: number) => {
    setRefundSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleProcessRefund = async () => {
    if (!refundLookupData || !refundReason.trim()) return;
    try {
      setRefundProcessing(true);
      const data: any = {
        transaction_id: refundLookupData.transaction.id,
        refund_type: refundType,
        reason: refundReason.trim(),
      };
      if (refundType === 'partial' && refundSelectedItems.size > 0) {
        data.items = Array.from(refundSelectedItems).map(id => ({ item_id: id }));
      } else if (refundType === 'partial' && refundCustomAmount) {
        data.custom_amount = parseFloat(refundCustomAmount);
      }
      const result = await posAPI.processRefund(data);
      // Card-present DEBIT: the refund is pending until the customer taps
      // their card at the terminal. Show the tap prompt and poll status.
      if (result.status === 'pending' && result.action_required === 'tap_card') {
        setRefundTapMessage(result.message || 'Have the customer tap their card at the reader.');
        pollTerminalRefund(result.refund_id);
        return;
      }
      await finishRefund(result);
    } catch (err: any) {
      setRefundLookupError(err.message || 'Refund failed');
    } finally {
      setRefundProcessing(false);
    }
  };

  // Load the receipt (if any) and show the result screen.
  const finishRefund = async (result: any) => {
    setRefundResult(result);
    if (result.refund_id) {
      try {
        const receiptData = await posAPI.getRefundReceiptData(result.refund_id);
        setRefundReceipt(receiptData);
      } catch { /* receipt data optional */ }
    }
  };

  // Poll the terminal debit refund until the customer taps (approved) or it
  // declines — mirrors pollCardPayment for sales.
  const pollTerminalRefund = async (refundId: number) => {
    try {
      const res = await posAPI.getRefundStatus(refundId);
      if (res.status === 'approved') {
        setRefundTapMessage('');
        setRefundProcessing(false);
        await finishRefund(res);
        return;
      }
      if (res.status === 'declined' || res.status === 'failed') {
        setRefundTapMessage('');
        setRefundProcessing(false);
        setRefundLookupError(res.message || res.error_message || 'Debit refund declined at the terminal.');
        return;
      }
      // Bounded wait elapsed at the server — stop polling and show a clear,
      // non-destructive terminal state. Do NOT auto-retry.
      if (res.status === 'unconfirmed') {
        setRefundTapMessage('');
        setRefundProcessing(false);
        setRefundLookupError(
          res.message ||
          'Refund not confirmed — check the terminal receipt before retrying. ' +
          'Do NOT re-run the refund if the reader printed an approval.');
        return;
      }
      // Still pending — keep polling.
      setRefundTapMessage(res.message || 'Waiting for the customer to tap their card...');
      setTimeout(() => pollTerminalRefund(refundId), 2000);
    } catch (err: any) {
      setRefundTapMessage('');
      setRefundProcessing(false);
      setRefundLookupError(err.message || 'Could not confirm the debit refund at the terminal.');
    }
  };

  const handlePrintRefundReceipt = async () => {
    if (!refundReceipt) return;
    try {
      setPrinting(true);
      const isCash = refundReceipt.payment_method === 'cash';
      await printRefundReceipt(refundReceipt, isCash);
    } catch (err: any) {
      setPrintError(err.message || 'Failed to print refund receipt');
    } finally {
      setPrinting(false);
    }
  };

  const handleRefundDone = () => {
    setViewMode('checkout');
    setRefundTxnNumber('');
    setRefundLookupData(null);
    setRefundLookupError('');
    setRefundResult(null);
    setRefundReceipt(null);
    setRefundReason('');
    setRefundTapMessage('');
    setRefundType('full');
    setRefundSelectedItems(new Set());
    setRefundCustomAmount('');
  };

  // Print receipt handler
  const handlePrintReceipt = async () => {
    if (!receipt) return;

    try {
      setPrinting(true);
      setPrintError('');

      // Open cash drawer for cash payments
      const shouldOpenDrawer = receipt.payments.some(p => p.method.toLowerCase() === 'cash');

      await printReceipt(receipt, shouldOpenDrawer);
      playBeep(true);
    } catch (err: any) {
      console.error('Print failed:', err);
      setPrintError(err.message || 'Failed to print. Is QZ Tray running?');
      playBeep(false);
    } finally {
      setPrinting(false);
    }
  };

  // Auto-print when receipt view is shown (if print checkbox is checked)
  useEffect(() => {
    if (viewMode === 'receipt' && receipt && printReceipt_) {
      // Small delay to let the view render first
      const timer = setTimeout(() => {
        handlePrintReceipt();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [viewMode, receipt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open cash drawer manually
  const handleOpenDrawer = async () => {
    try {
      await openCashDrawer();
      playBeep(true);
    } catch (err) {
      console.error('Failed to open drawer:', err);
      playBeep(false);
    }
  };

  // Simple beep function
  const playBeep = (success: boolean) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = success ? 800 : 300;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Audio not supported
    }
  };

  // Quick cash buttons
  const quickCashAmounts = [1, 5, 10, 20, 50, 100];

  const handleQuickCash = (amount: number) => {
    const current = parseFloat(cashTendered) || 0;
    setCashTendered((current + amount).toFixed(2));
  };

  if (!isAuthenticated) return null;

  if (sessionLoading) {
    return (
      <div style={styles.page}>
        <StoreHeader />
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>Loading POS...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Mill Requests Morning Popup */}
      {showMillRequestPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={dismissMillRequestPopup}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '32px',
            maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏭</div>
            <h2 style={{ margin: '0 0 8px', color: '#1a4d1a', fontSize: '22px' }}>
              Mill Requests Waiting
            </h2>
            <p style={{ color: '#666', fontSize: '16px', margin: '0 0 20px' }}>
              You have <strong style={{ color: '#e65100' }}>{millRequestUrgent}</strong> item{millRequestUrgent !== 1 ? 's' : ''} to pull for mill orders today
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => { dismissMillRequestPopup(); navigate('/store/orders?tab=mill_requests'); }}
                style={{
                  padding: '12px 24px', background: '#1a4d1a', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '16px',
                  fontWeight: '600', cursor: 'pointer',
                }}
              >
                Go to Mill Requests
              </button>
              <button
                onClick={dismissMillRequestPopup}
                style={{
                  padding: '12px 24px', background: '#f5f5f5', color: '#666',
                  border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-hiding header */}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease',
        }}
        onMouseEnter={() => {
          if (headerTimeoutRef.current) clearTimeout(headerTimeoutRef.current);
          setHeaderVisible(true);
        }}
        onMouseLeave={() => {
          headerTimeoutRef.current = setTimeout(() => setHeaderVisible(false), 3000);
        }}
      >
        <StoreHeader />
      </div>

      {/* Thin trigger bar when header hidden */}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: '8px', backgroundColor: '#1a4d1a', zIndex: 999,
          cursor: 'pointer',
        }}
        onMouseEnter={() => {
          if (headerTimeoutRef.current) clearTimeout(headerTimeoutRef.current);
          setHeaderVisible(true);
        }}
      />

      {/* Open Session Modal */}
      {showOpenSessionModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Open Drawer</h2>
            <p style={styles.modalText}>Enter the starting amounts in the drawer:</p>
            {registers.length > 1 && (
              <>
                <label style={styles.inputLabel}>Register:</label>
                <select
                  value={selectedRegisterId || ''}
                  onChange={(e) => setSelectedRegisterId(Number(e.target.value) || null)}
                  style={{ ...styles.cashInput, cursor: 'pointer' }}
                >
                  <option value="">Select register...</option>
                  {registers.map(r => (
                    <option key={r.id} value={r.id}>{r.name}{r.location ? ` (${r.location})` : ''}</option>
                  ))}
                </select>
              </>
            )}
            {registers.length === 1 && (
              <p style={{ ...styles.modalText, fontWeight: 'bold', color: '#1a4d1a' }}>
                Register: {registers[0].name}
              </p>
            )}
            <label style={styles.inputLabel}>Cash:</label>
            <input
              type="number"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              placeholder="0.00"
              style={styles.cashInput}
              autoFocus
            />
            <label style={{ ...styles.inputLabel, marginTop: '0.75rem' }}>Checks (if any):</label>
            <input
              type="number"
              value={openingCheckFloat}
              onChange={(e) => setOpeningCheckFloat(e.target.value)}
              placeholder="0.00"
              style={styles.cashInput}
            />
            <button
              onClick={handleOpenSession}
              disabled={loading || (registers.length > 1 && !selectedRegisterId)}
              style={{
                ...styles.primaryBtn,
                marginTop: '1rem',
                opacity: (registers.length > 1 && !selectedRegisterId) ? 0.5 : 1,
              }}
            >
              {loading ? 'Opening...' : 'Start Shift'}
            </button>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>
              {discountTarget === 'item' ? 'Item Discount' : 'Cart Discount'}
            </h2>
            <div style={styles.discountTypeToggle}>
              <button
                onClick={() => setDiscountType('percent')}
                style={discountType === 'percent' ? styles.discountTypeActive : styles.discountTypeBtn}
              >
                % Percent
              </button>
              <button
                onClick={() => setDiscountType('dollar')}
                style={discountType === 'dollar' ? styles.discountTypeActive : styles.discountTypeBtn}
              >
                $ Dollar
              </button>
            </div>
            <input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percent' ? 'Enter %' : 'Enter $'}
              style={styles.cashInput}
              autoFocus
              min="0"
              max={discountType === 'percent' ? '100' : undefined}
              step="any"
            />
            <div style={styles.modalBtnRow}>
              <button
                onClick={() => setShowDiscountModal(false)}
                style={styles.secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyDiscount}
                disabled={!discountValue || parseFloat(discountValue) <= 0}
                style={{
                  ...styles.primaryBtn,
                  opacity: !discountValue || parseFloat(discountValue) <= 0 ? 0.5 : 1,
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reservation/oversell guard block — the "Charles" correction */}
      {guardBlock && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            {guardBlock.code === 'oversell' ? (
              <>
                <h2 style={styles.modalTitle}>Not enough counted in</h2>
                <p style={{ marginBottom: 12 }}>
                  <strong>{guardBlock.product}</strong>: only {guardBlock.available} in
                  the system, sale needs {guardBlock.requested}. If the stock is
                  physically on the shelf, count it in and continue.
                </p>
                {guardBlock.productId != null ? (
                  <>
                    <label style={{ fontSize: 14, fontWeight: 600 }}>Add to inventory</label>
                    <input
                      type="number"
                      value={guardAddQty}
                      onChange={(e) => setGuardAddQty(e.target.value)}
                      style={styles.cashInput}
                      autoFocus
                      min="1"
                      step="1"
                    />
                    <div style={styles.modalBtnRow}>
                      <button
                        onClick={() => { setGuardBlock(null); setGuardAddQty(''); }}
                        style={styles.secondaryBtn}
                        disabled={guardBusy}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleGuardCountIn}
                        disabled={guardBusy || !guardAddQty || parseInt(guardAddQty, 10) <= 0}
                        style={{
                          ...styles.primaryBtn,
                          opacity: guardBusy || !guardAddQty || parseInt(guardAddQty, 10) <= 0 ? 0.5 : 1,
                        }}
                      >
                        {guardBusy ? 'Working…' : 'Add to inventory & continue'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={styles.modalBtnRow}>
                    <button onClick={() => setGuardBlock(null)} style={styles.secondaryBtn}>
                      Close
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 style={styles.modalTitle}>Reserved for the mill</h2>
                <p style={{ marginBottom: 12 }}>
                  <strong>{guardBlock.product}</strong>: {guardBlock.available} available —
                  the rest is reserved for a pending mill order. This is NOT an
                  uncounted-stock fix. A manager override will sell into the
                  reservation and flag the mill's pull SHORT.
                </p>
                <div style={styles.modalBtnRow}>
                  <button
                    onClick={() => setGuardBlock(null)}
                    style={styles.secondaryBtn}
                    disabled={guardBusy}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGuardOverride}
                    disabled={guardBusy}
                    style={{ ...styles.primaryBtn, backgroundColor: '#a33', opacity: guardBusy ? 0.5 : 1 }}
                  >
                    {guardBusy ? 'Working…' : 'Manager override — sell reserved'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Weight/Price Entry Modal */}
      {/* weight_tracked tag-weight prompt — PRICED PER POUND. Shows the per-lb rate
          and the live computed total as the cashier types. Separate from the
          BYOC/variable modal below so that path is byte-for-byte untouched. */}
      {showTagWeightModal && tagWeightProduct && (() => {
        const rate = parseFloat(tagWeightProduct.effective_price || tagWeightProduct.retail_price) || 0;
        const typed = parseFloat(tagWeightValue) || 0;
        const computed = rate * typed;
        return (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Enter Tag Weight</h2>
            <p style={styles.modalText}>{tagWeightProduct.name}</p>
            <p style={styles.priceText}>${rate.toFixed(2)} / lb</p>
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.inputLabel}>Weight from package tag (lbs)</label>
              <input
                type="number"
                value={tagWeightValue}
                onChange={(e) => setTagWeightValue(e.target.value)}
                placeholder="e.g. 0.58"
                style={styles.cashInput}
                step="0.01"
                min="0"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && tagWeightValue) handleTagWeightSubmit(); }}
              />
            </div>
            {typed > 0 && rate > 0 && (
              <div style={styles.calculatedTotal}>
                <span>${rate.toFixed(2)}/lb × {typed.toFixed(2)} =</span>
                <span style={{ fontWeight: 'bold', fontSize: '24px' }}>${computed.toFixed(2)}</span>
              </div>
            )}
            <div style={styles.modalBtnRow}>
              <button
                onClick={() => {
                  setShowTagWeightModal(false);
                  setTagWeightProduct(null);
                  setTagWeightValue('');
                }}
                style={styles.secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleTagWeightSubmit}
                disabled={loading || !tagWeightValue}
                style={{ ...styles.primaryBtn, ...(loading || !tagWeightValue ? { opacity: 0.5 } : {}) }}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {showWeightModal && weightProduct && (() => {
        const isVariablePrice = weightProduct.is_variable_price ||
            (parseFloat(weightProduct.effective_price || '0') === 0 && parseFloat(weightProduct.retail_price) === 0);
        const displayPrice = parseFloat(priceValue) || parseFloat(weightProduct.effective_price || weightProduct.retail_price) || 0;
        const displayWeight = parseFloat(weightValue) || 0;
        const calculatedTotal = displayPrice * displayWeight;

        return (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <h2 style={styles.modalTitle}>
                {isVariablePrice ? 'Enter Price & Weight' : 'Enter Weight'}
              </h2>
              <p style={styles.modalText}>{weightProduct.name}</p>

              {/* Price Input - Only for variable price items */}
              {isVariablePrice && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={styles.inputLabel}>Price per lb</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold' }}>$</span>
                    <input
                      type="number"
                      value={priceValue}
                      onChange={(e) => setPriceValue(e.target.value)}
                      placeholder="0.00"
                      style={styles.cashInput}
                      step="0.01"
                      min="0"
                      autoFocus
                    />
                    <span style={{ fontSize: '16px', color: '#6B7280' }}>/ lb</span>
                  </div>
                </div>
              )}

              {/* Fixed price display for BYOC */}
              {!isVariablePrice && (
                <p style={styles.priceText}>${parseFloat(weightProduct.effective_price || weightProduct.retail_price).toFixed(2)} / lb</p>
              )}

              {/* Weight Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.inputLabel}>Weight (lbs)</label>
                <input
                  type="number"
                  value={weightValue}
                  onChange={(e) => setWeightValue(e.target.value)}
                  placeholder="0.00"
                  style={styles.cashInput}
                  step="0.01"
                  min="0"
                  autoFocus={!isVariablePrice}
                />
              </div>

              {/* Calculated Total */}
              {displayWeight > 0 && displayPrice > 0 && (
                <div style={styles.calculatedTotal}>
                  <span>Total:</span>
                  <span style={{ fontWeight: 'bold', fontSize: '24px' }}>
                    ${calculatedTotal.toFixed(2)}
                  </span>
                </div>
              )}

              <div style={styles.modalBtnRow}>
                <button
                  onClick={() => {
                    setShowWeightModal(false);
                    setWeightProduct(null);
                    setPriceValue('');
                    setWeightValue('');
                  }}
                  style={styles.secondaryBtn}
                >
                  Cancel
                </button>
                <button
                  onClick={handleWeightSubmit}
                  disabled={loading || !weightValue || (isVariablePrice && !priceValue)}
                  style={{
                    ...styles.primaryBtn,
                    ...(loading || !weightValue || (isVariablePrice && !priceValue) ? { opacity: 0.5 } : {}),
                  }}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bridge Club signup nudge — intercepts Pay on unattributed
          walk-ins, then routes into the existing Customer Lookup modal. */}
      <BridgeClubPromptModal
        isOpen={showBcPrompt}
        onLookup={handleBcPromptLookup}
        onSkip={handleBcPromptSkip}
        onCancel={handleBcPromptCancel}
      />

      {/* Invoice collection (A1): open-invoices list + amount/note step.
          Pulls a "Payment — INV-XXXX" line into the cart at the invoice's
          BALANCE (editable down to a partial), never the order total. */}
      {invoicePanelTarget && (
        <POSInvoicePanel
          memberId={invoicePanelTarget.memberId ?? null}
          customerId={invoicePanelTarget.customerId ?? null}
          customerName={invoicePanelTarget.name}
          onApply={handleApplyInvoicePayment}
          onClose={() => setInvoicePanelTarget(null)}
        />
      )}

      {/* Customer Lookup Modal */}
      {showCustomerModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: '500px' }}>
            <h2 style={styles.modalTitle}>
              {linkWalkinVisitorId != null ? 'Link Walk-in to Member' : 'Find Customer'}
            </h2>
            <p style={styles.modalText}>
              {linkWalkinVisitorId != null
                ? 'Pick the existing member to credit this walk-in’s purchases to'
                : 'Search by name, phone, or member code'}
            </p>
            <input
              type="text"
              value={customerSearchQuery}
              onChange={(e) => handleCustomerSearch(e.target.value)}
              placeholder="Search..."
              style={styles.barcodeInput}
              autoFocus
            />
            <div style={styles.customerResults}>
              {customerSearching ? (
                <div style={styles.loadingText}>Searching...</div>
              ) : customerSearchResults.length === 0 && customerSearchQuery.length >= 2 ? (
                <div>
                  <div style={styles.noResults}>No members found</div>
                  {!showNewBcForm && (
                    <button
                      onClick={openNewBcForm}
                      style={{
                        marginTop: 8,
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ECFDF5',
                        border: '1px solid #10B981',
                        borderRadius: 6,
                        color: '#065F46',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}
                    >
                      + Add new Bridge Club customer
                    </button>
                  )}
                </div>
              ) : (
                customerSearchResults.map((member, idx) => {
                  // Three tiers: BC member, walk-in (UnmatchedVisitor),
                  // wholesale Customer with pending Will-Call orders.
                  // Wholesale rows render with a teal accent + per-order
                  // "Load in POS" buttons; clicking the row itself is
                  // disabled because wholesale needs the order-level
                  // load action, not customer attachment.
                  if (member.is_wholesale) {
                    return (
                      <div
                        key={`wh-${member.id ?? idx}`}
                        style={{
                          ...styles.customerResult,
                          borderLeft: '3px solid #0E7490',
                          paddingLeft: 8,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          gap: 6,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          <span style={styles.customerName}>{member.name}</span>
                          <span style={{
                            fontSize: 11,
                            color: '#0E7490',
                            fontWeight: 700,
                            background: '#CFFAFE',
                            padding: '2px 6px',
                            borderRadius: 4,
                          }}>
                            WHOLESALE
                          </span>
                          {member.pending_orders?.some((o) => o.is_tax_exempt) && (
                            <span style={{
                              fontSize: 11,
                              color: '#7C2D12',
                              fontWeight: 700,
                              background: '#FED7AA',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}>
                              TAX EXEMPT
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                          {member.contact_person} · {member.phone}
                        </div>
                        {(member.pending_orders ?? []).map((o) => (
                          <button
                            key={`wo-${o.id}`}
                            onClick={() => handleLoadWholesaleOrder(o.id)}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 8,
                              padding: '8px 12px',
                              background: o.status_warning ? '#FEF3C7' : '#ECFDF5',
                              border: `1px solid ${o.status_warning ? '#F59E0B' : '#10B981'}`,
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: 13,
                              textAlign: 'left',
                            }}
                            title={o.status_warning
                              ? `Status: ${o.status} — order may not be physically ready yet`
                              : `Status: ${o.status} — ready for pickup`}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>
                                #{o.order_number} · ${o.total.toFixed(2)}
                                {o.status_warning && (
                                  <span style={{ marginLeft: 6, color: '#92400E', fontSize: 11 }}>
                                    ⚠ {o.status}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: '#374151' }}>
                                {o.item_count} item{o.item_count === 1 ? '' : 's'}
                                {o.items_preview ? ` · ${o.items_preview}` : ''}
                              </div>
                            </div>
                            <span style={{
                              padding: '4px 10px',
                              background: '#059669',
                              color: 'white',
                              borderRadius: 4,
                              fontWeight: 600,
                              fontSize: 12,
                            }}>
                              Load in POS
                            </span>
                          </button>
                        ))}
                        {/* Invoice collection (A1): open-invoices panel for
                            this wholesale Customer — balance-due payments,
                            distinct from the order-pickup buttons above. */}
                        <button
                          key={`winv-${member.id}`}
                          onClick={() => {
                            setShowCustomerModal(false);
                            setInvoicePanelTarget({ customerId: member.id as number, name: member.name });
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#EFF6FF',
                            border: '1px solid #3B82F6',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#1D4ED8',
                            textAlign: 'center',
                          }}
                        >
                          💳 Open Invoices / Pay Balance
                        </button>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={member.is_unmatched ? `uv-${member.unmatched_visitor_id}` : (member.id ?? `idx-${idx}`)}
                      onClick={() => handleAttachCustomer(member)}
                      style={
                        member.is_unmatched
                          ? { ...styles.customerResult, borderLeft: '3px solid #F59E0B', paddingLeft: 8, display: 'flex', alignItems: 'center' }
                          : { ...styles.customerResult, display: 'flex', alignItems: 'center' }
                      }
                    >
                      {member.profile_picture && (
                        <img
                          src={member.profile_picture}
                          alt={member.name}
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: 8,
                            flexShrink: 0,
                            border: '2px solid #e5e7eb',
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                      <div style={styles.customerInfo}>
                        <span style={styles.customerName}>{member.name}</span>
                        {member.is_unmatched ? (
                          <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>WALK-IN</span>
                        ) : (
                          <span style={styles.customerCode}>{member.member_code}</span>
                        )}
                      </div>
                      {member.is_unmatched ? (
                        <div style={{ fontSize: 12, color: '#92400E' }}>
                          {member.visit_count ?? 0} visit{member.visit_count === 1 ? '' : 's'}
                          {' • '}
                          ${member.total_spend ?? '0'} total
                          {member.card_last_four ? ` • ···· ${member.card_last_four}` : ''}
                          {member.last_visit ? (
                            <div style={{ fontSize: 11, color: '#B45309' }}>Last visit: {member.last_visit}</div>
                          ) : null}
                        </div>
                      ) : (
                        <div style={styles.customerContact}>
                          {member.phone && <span>{member.phone}</span>}
                          {member.member_since && <span style={styles.memberSince}>Member since {member.member_since}</span>}
                        </div>
                      )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            {/* Inline "Add new Bridge Club customer" form. Opens from
                the zero-results affordance above. 4 required fields,
                duplicate-email and phone-match UX inline. */}
            {showNewBcForm && (
              <div style={{
                marginTop: 12,
                padding: 12,
                background: '#F0FDF4',
                border: '1px solid #86EFAC',
                borderRadius: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <strong style={{ color: '#065F46', fontSize: 14 }}>
                    Enroll new Bridge Club member
                  </strong>
                  <button
                    onClick={closeNewBcForm}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6B7280' }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <input
                    type="text"
                    placeholder="First name"
                    value={newBcFirstName}
                    onChange={(e) => setNewBcFirstName(e.target.value)}
                    style={{ padding: 8, border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14 }}
                  />
                  <input
                    type="text"
                    placeholder="Last name"
                    value={newBcLastName}
                    onChange={(e) => setNewBcLastName(e.target.value)}
                    style={{ padding: 8, border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14 }}
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email (for welcome + login)"
                  value={newBcEmail}
                  onChange={(e) => setNewBcEmail(e.target.value)}
                  style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14, marginBottom: 8 }}
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={newBcPhone}
                  onChange={(e) => setNewBcPhone(e.target.value)}
                  style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14, marginBottom: 8 }}
                />
                {newBcError && (
                  <div style={{
                    padding: 8,
                    background: '#FEF2F2',
                    border: '1px solid #FCA5A5',
                    borderRadius: 4,
                    color: '#991B1B',
                    fontSize: 13,
                    marginBottom: 8,
                  }}>
                    {newBcError}
                  </div>
                )}
                {/* 409 duplicate_email: backend returned the existing
                    member as a POSCustomer. One-click attach. */}
                {newBcExistingMember && (
                  <button
                    onClick={() => {
                      const existing = newBcExistingMember;
                      closeNewBcForm();
                      handleAttachCustomer(existing);
                    }}
                    style={{
                      width: '100%',
                      padding: 10,
                      background: '#0E7490',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: 'pointer',
                      marginBottom: 8,
                    }}
                  >
                    Use {newBcExistingMember.name}
                  </button>
                )}
                {/* 200 warning='phone_match': render each candidate
                    plus a Create-Anyway button. */}
                {newBcPhoneCandidates.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {newBcPhoneCandidates.map((c) => (
                      <button
                        key={`pc-${c.id}`}
                        onClick={() => {
                          closeNewBcForm();
                          handleAttachCustomer(c);
                        }}
                        style={{
                          width: '100%',
                          padding: 8,
                          background: '#FFFFFF',
                          border: '1px solid #0E7490',
                          color: '#0E7490',
                          borderRadius: 4,
                          fontWeight: 600,
                          cursor: 'pointer',
                          marginBottom: 4,
                          textAlign: 'left',
                        }}
                      >
                        Use {c.name} ({c.member_code})
                      </button>
                    ))}
                    <button
                      onClick={() => submitNewBcForm(true)}
                      disabled={newBcSubmitting}
                      style={{
                        width: '100%',
                        padding: 8,
                        background: '#F59E0B',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontWeight: 600,
                        cursor: newBcSubmitting ? 'wait' : 'pointer',
                        marginTop: 4,
                      }}
                    >
                      Create new anyway
                    </button>
                  </div>
                )}
                {/* Primary submit — only shown when no existing-member
                    UX is active (otherwise the user should pick one
                    of the existing options). */}
                {!newBcExistingMember && newBcPhoneCandidates.length === 0 && (
                  <button
                    onClick={() => submitNewBcForm(false)}
                    disabled={newBcSubmitting}
                    style={{
                      width: '100%',
                      padding: 10,
                      background: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: newBcSubmitting ? 'wait' : 'pointer',
                    }}
                  >
                    {newBcSubmitting ? 'Adding…' : 'Add as Bridge Club member'}
                  </button>
                )}
              </div>
            )}
            <div style={styles.modalBtnRow}>
              <button
                onClick={() => {
                  setShowCustomerModal(false);
                  setCustomerSearchQuery('');
                  setCustomerSearchResults([]);
                  setLinkWalkinVisitorId(null);
                  closeNewBcForm();
                }}
                style={styles.secondaryBtn}
              >
                Cancel
              </button>
              {selectedCustomer && (
                <button
                  onClick={() => handleAttachCustomer(null)}
                  style={{ ...styles.secondaryBtn, backgroundColor: '#ffebee', color: '#c62828' }}
                >
                  Clear Customer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* End-shift: keep-or-discard parked carts (option C) */}
      {showParkedPrompt && (
        <ShiftCloseParkedPrompt
          count={parkedCarts.length}
          onKeep={handleKeepParkedCarts}
          onDiscard={handleDiscardAllParkedCarts}
        />
      )}

      {/* Shift Closeout Modal */}
      {showCloseoutModal && sessionSummary && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: '520px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
            <h2 style={{ ...styles.modalTitle, padding: '1rem 1.5rem 0.5rem', margin: 0, flexShrink: 0 }}>Close Shift</h2>

            {/* Scrollable content area */}
            <div style={{ overflowY: 'auto', padding: '0 1.5rem', flex: 1 }}>
              {/* Session Summary — compact row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e0e0e0', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                <span>Started: {new Date(sessionSummary.opened_at).toLocaleTimeString()}</span>
                <span>{sessionSummary.transaction_count} txns</span>
                <span style={{ fontWeight: 'bold' }}>${sessionSummary.totals.sales.toFixed(2)}</span>
              </div>

              {/* Payment Breakdown — compact */}
              <div style={{ ...styles.summarySection, padding: '0.4rem 0', marginBottom: '0.5rem' }}>
                <h3 style={{ ...styles.summaryTitle, margin: '0 0 0.3rem', fontSize: '0.9rem' }}>Payment Breakdown</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15rem 1rem', fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Cash:</span><span>${sessionSummary.payment_breakdown.cash.toFixed(2)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Credit:</span><span>${sessionSummary.payment_breakdown.credit.toFixed(2)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Debit:</span><span>${sessionSummary.payment_breakdown.debit.toFixed(2)}</span></div>
                  {sessionSummary.payment_breakdown.check > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Check:</span><span>${sessionSummary.payment_breakdown.check.toFixed(2)}</span></div>
                  )}
                  {(sessionSummary.payment_breakdown.account ?? 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>On Account:</span><span>${(sessionSummary.payment_breakdown.account ?? 0).toFixed(2)}</span></div>
                  )}
                </div>
                {(sessionSummary.tab_paydowns ?? 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem', fontSize: '0.82rem', color: '#666' }}>
                    <span>Tab Pay-downs (settlement, incl. above):</span><span>${(sessionSummary.tab_paydowns ?? 0).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Voids & Refunds — compact */}
              {((sessionSummary.voided_count > 0) || (sessionSummary.refund_count && sessionSummary.refund_count > 0)) && (
                <div style={{ display: 'flex', gap: '1.5rem', padding: '0.3rem 0', borderBottom: '1px solid #e0e0e0', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  {sessionSummary.voided_count > 0 && (
                    <span>Voids: {sessionSummary.voided_count}</span>
                  )}
                  {sessionSummary.refund_count && sessionSummary.refund_count > 0 && (
                    <span style={{ color: '#d32f2f' }}>Refunds ({sessionSummary.refund_count}): -${sessionSummary.refund_total?.toFixed(2)}</span>
                  )}
                </div>
              )}

              {/* Reconciliation — Cash and Check side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
                {/* Cash column */}
                <div>
                  <h3 style={{ ...styles.summaryTitle, margin: '0 0 0.3rem', fontSize: '0.9rem' }}>Cash in Drawer</h3>
                  <div style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Opening:</span><span>${sessionSummary.opening_cash.toFixed(2)}</span></div>
                    {(sessionSummary.petty_cash_total || 0) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d32f2f' }}><span>Petty Cash:</span><span>-${(sessionSummary.petty_cash_total || 0).toFixed(2)}</span></div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Expected:</span><span style={{ fontWeight: 'bold', color: '#1a4d1a' }}>${sessionSummary.expected_cash.toFixed(2)}</span></div>
                  </div>
                  <input
                    type="number"
                    value={closingCash}
                    onChange={(e) => setClosingCash(e.target.value)}
                    placeholder="Counted cash"
                    style={{ ...styles.cashInput, padding: '0.6rem', fontSize: '1.1rem' }}
                    step="0.01"
                  />
                  {closingCash && !isNaN(parseFloat(closingCash)) && (
                    <div style={{
                      padding: '0.3rem 0.5rem', borderRadius: '6px', textAlign: 'center', fontSize: '0.85rem', marginTop: '0.3rem',
                      backgroundColor: Math.abs(parseFloat(closingCash) - sessionSummary.expected_cash) < 1 ? '#e8f5e9' : '#fff3e0',
                      color: Math.abs(parseFloat(closingCash) - sessionSummary.expected_cash) < 1 ? '#2e7d32' : '#e65100',
                    }}>
                      Variance: ${(parseFloat(closingCash) - sessionSummary.expected_cash).toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Check column — always shown */}
                <div>
                  <h3 style={{ ...styles.summaryTitle, margin: '0 0 0.3rem', fontSize: '0.9rem' }}>Checks in Drawer</h3>
                  <div style={{ fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Opening:</span><span>${(sessionSummary.opening_check_float || 0).toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Expected:</span><span style={{ fontWeight: 'bold', color: '#1a4d1a' }}>${(sessionSummary.expected_checks || 0).toFixed(2)}</span></div>
                  </div>
                  <input
                    type="number"
                    value={closingChecks}
                    onChange={(e) => setClosingChecks(e.target.value)}
                    placeholder="Counted checks"
                    style={{ ...styles.cashInput, padding: '0.6rem', fontSize: '1.1rem' }}
                    step="0.01"
                  />
                  {closingChecks && !isNaN(parseFloat(closingChecks)) && (
                    <div style={{
                      padding: '0.3rem 0.5rem', borderRadius: '6px', textAlign: 'center', fontSize: '0.85rem', marginTop: '0.3rem',
                      backgroundColor: Math.abs(parseFloat(closingChecks) - (sessionSummary.expected_checks || 0)) < 1 ? '#e8f5e9' : '#fff3e0',
                      color: Math.abs(parseFloat(closingChecks) - (sessionSummary.expected_checks || 0)) < 1 ? '#2e7d32' : '#e65100',
                    }}>
                      Variance: ${(parseFloat(closingChecks) - (sessionSummary.expected_checks || 0)).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ ...styles.inputLabel, fontSize: '0.85rem', marginBottom: '0.25rem' }}>Notes (optional):</label>
                <input
                  type="text"
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  placeholder="Any notes about the shift..."
                  style={{ ...styles.barcodeInput, padding: '0.5rem' }}
                />
              </div>
            </div>

            {/* Sticky footer buttons */}
            <div style={{ ...styles.modalBtnRow, padding: '0.75rem 1.5rem', borderTop: '1px solid #e0e0e0', flexShrink: 0, backgroundColor: '#fff', borderRadius: '0 0 16px 16px' }}>
              <button
                onClick={() => setShowCloseoutModal(false)}
                style={styles.secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleCloseShift}
                disabled={loading || !closingCash}
                style={{
                  ...styles.primaryBtn,
                  opacity: loading || !closingCash ? 0.5 : 1,
                }}
              >
                {loading ? 'Closing...' : 'Close Shift'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Petty Cash Modal */}
      {showPettyCashModal && session && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: '500px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
            <h2 style={{ ...styles.modalTitle, padding: '1rem 1.5rem 0.5rem', margin: 0, flexShrink: 0 }}>Petty Cash Withdrawal</h2>
            <div style={{ overflowY: 'auto', padding: '0 1.5rem', flex: 1 }}>
              {/* Cash in Drawer display */}
              {pettyCashDrawerAmount != null && (
                <div style={{ textAlign: 'center', padding: '0.75rem', backgroundColor: '#e8f5e9', borderRadius: '8px', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>Cash in Drawer</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1a4d1a' }}>${pettyCashDrawerAmount.toFixed(2)}</div>
                </div>
              )}

              {pettyCashError && (
                <div style={{ padding: '0.5rem', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  {pettyCashError}
                </div>
              )}

              {/* Amount */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ ...styles.inputLabel, fontSize: '0.85rem' }}>Amount ($)</label>
                <input
                  type="number"
                  value={pettyCashAmount}
                  onChange={(e) => setPettyCashAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  style={{ ...styles.cashInput, padding: '0.6rem', fontSize: '1.1rem' }}
                />
              </div>

              {/* Category */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ ...styles.inputLabel, fontSize: '0.85rem' }}>Category</label>
                <select
                  value={pettyCashCategory}
                  onChange={(e) => setPettyCashCategory(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem', fontSize: '1rem', border: '2px solid #ddd', borderRadius: '8px', minHeight: '48px' }}
                >
                  <option value="supplies">Office/Store Supplies</option>
                  <option value="fuel">Fuel/Mileage</option>
                  <option value="maintenance">Equipment Maintenance</option>
                  <option value="food">Meals/Food</option>
                  <option value="postage">Postage/Shipping</option>
                  <option value="deposit">Bank Deposit</option>
                  <option value="change">Change Run (Getting Change)</option>
                  <option value="vendor">Vendor Payment</option>
                  <option value="refund">Customer Refund (Cash)</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ ...styles.inputLabel, fontSize: '0.85rem' }}>
                  Description {pettyCashCategory === 'other' ? '(required)' : '(optional)'}
                </label>
                <textarea
                  value={pettyCashDescription}
                  onChange={(e) => setPettyCashDescription(e.target.value)}
                  placeholder="What is this withdrawal for?"
                  rows={2}
                  style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '2px solid #ddd', borderRadius: '8px', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button
                  onClick={() => setShowPettyCashModal(false)}
                  style={{ ...styles.secondaryBtn, flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePettyCashWithdraw}
                  disabled={pettyCashLoading || !pettyCashAmount}
                  style={{
                    flex: 1, padding: '0.75rem', fontSize: '1rem', fontWeight: 'bold',
                    backgroundColor: '#E73C3E', color: '#fff', border: 'none', borderRadius: '8px',
                    cursor: pettyCashLoading || !pettyCashAmount ? 'not-allowed' : 'pointer',
                    opacity: pettyCashLoading || !pettyCashAmount ? 0.5 : 1,
                    minHeight: '48px',
                  }}
                >
                  {pettyCashLoading ? 'Processing...' : 'WITHDRAW'}
                </button>
              </div>

              {/* Session withdrawal list */}
              {pettyCashWithdrawals.length > 0 && (
                <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '0.75rem' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>This Shift&apos;s Withdrawals</h3>
                  {pettyCashWithdrawals.map((w) => (
                    <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem' }}>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>{w.category_display}</span>
                        {w.description && <span style={{ color: '#666', marginLeft: '0.5rem' }}>{w.description}</span>}
                      </div>
                      <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>-${w.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <main style={styles.main}>
        {/* Error Display */}
        {error && (
          <div style={styles.errorBanner}>
            {error}
            <button onClick={() => setError('')} style={styles.errorClose}>X</button>
          </div>
        )}

        {/* Bag-credit auto-apply alert — loud, never silent (Brad's ruling) */}
        {bagCreditNotice && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, margin: '8px 0', padding: '10px 14px', borderRadius: 8,
            background: '#DCFCE7', border: '1px solid #16A34A', color: '#166534',
            fontWeight: 600,
          }}>
            <span>🎟️ Bag credit applied: {bagCreditNotice}</span>
            <button onClick={() => setBagCreditNotice(null)}
              style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontWeight: 700 }}>
              ✕
            </button>
          </div>
        )}

        {/* Checkout View */}
        {viewMode === 'checkout' && (
          <div style={styles.checkoutLayout}>
            {/* Left: Products Area (70%) */}
            <div style={styles.productsArea}>
              {/* Search/Barcode Input Row */}
              <form onSubmit={handleBarcodeSubmit} style={styles.barcodeForm}>
                <input
                  ref={barcodeInputRef}
                  type="text"
                  value={barcodeInput}
                  onChange={(e) => {
                    setBarcodeInput(e.target.value);
                    if (error) setError(''); // Clear error when typing
                  }}
                  placeholder="Scan barcode or search..."
                  style={styles.barcodeInput}
                />
                <button type="submit" style={styles.scanBtn}>
                  SCAN
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Transfer barcode input to search query for text search
                    if (barcodeInput.trim()) {
                      setSearchQuery(barcodeInput.trim());
                      flushSearch();
                    }
                    setViewMode('search');
                  }}
                  style={styles.searchBtn}
                >
                  SEARCH
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  style={styles.fullscreenBtn}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                  {isFullscreen ? '\u22A0' : '\u26F6'}
                </button>
              </form>

              {/* Hierarchical Category Navigation */}
              <POSCategoryNav
                navState={navState}
                availableAnimalTypes={availableAnimalTypes}
                onSelectFamily={handleSelectFamily}
                onSelectCategory={handleSelectCategory}
                onSelectAnimalType={handleSelectAnimalType}
                onBack={handleNavBack}
                onShowAll={handleShowAllProducts}
              />

              {/* Product Grid */}
              <div style={styles.productGridSection}>
                <POSProductGrid
                  products={allProducts}
                  navState={navState}
                  onAddToCart={addProductToCart}
                  loading={productsLoading}
                />
              </div>
            </div>

            {/* Right: Cart */}
            <div style={styles.cartArea}>
              {/* Scroll region: customer info + bins + loyalty + cart items
                  scroll together, leaving cartFooter anchored (Athea/Charles fix). */}
              <div style={styles.cartScrollRegion}>
              <div style={styles.cartHeader}>
                <div>
                  <h2 style={styles.cartTitle}>Current Sale{session?.register_name ? ` — ${session.register_name}` : ''}</h2>
                  {transaction && (
                    <span style={styles.transactionNumber}>#{transaction.transaction_number}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                  <button
                    onClick={() => { setViewMode('refund'); setRefundTxnNumber(''); setRefundLookupData(null); setRefundResult(null); setRefundLookupError(''); }}
                    style={{ ...styles.refundBtn, flex: 1 }}
                    title="Process Refund"
                  >
                    REFUND
                  </button>
                  <button
                    onClick={() => navigate('/store/pos/daily-report')}
                    style={{ ...styles.closeShiftBtn, backgroundColor: '#1565c0', flex: 1 }}
                    title="Daily Report"
                  >
                    REPORT
                  </button>
                  <button
                    onClick={handleOpenPettyCash}
                    style={{ ...styles.closeShiftBtn, backgroundColor: 'transparent', color: '#508590', border: '2px solid #508590', fontWeight: 'bold', flex: 1 }}
                    title="Petty Cash Withdrawal"
                  >
                    PETTY $
                  </button>
                  <button
                    onClick={handleOpenCloseout}
                    style={{ ...styles.closeShiftBtn, flex: 1 }}
                    title="Close Shift"
                  >
                    END SHIFT
                  </button>
                </div>
              </div>

              {/* Customer Display */}
              {transaction && (
                <button
                  onClick={() => setShowCustomerModal(true)}
                  style={styles.customerBtn}
                >
                  {selectedCustomer ? (
                    <>
                      {selectedCustomer.profile_picture ? (
                        <img
                          src={selectedCustomer.profile_picture}
                          alt={selectedCustomer.name}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: 8,
                            border: '2px solid #2D5A27',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <span style={styles.customerIcon}>&#128100;</span>
                      )}
                      <span>{selectedCustomer.name}</span>
                      <span style={styles.customerMemberCode}>{selectedCustomer.member_code}</span>
                    </>
                  ) : (
                    <>
                      <span style={styles.customerIcon}>+</span>
                      <span>Add Customer</span>
                    </>
                  )}
                </button>
              )}

              {/* Tax-exemption certificate banner (Build B) — DISPLAY ONLY.
                  It never sets tax_exempt or touches a line; it tells the
                  cashier the cert status so THEY can decide. Expired is
                  visually distinct (red) and never reads as valid. */}
              {selectedCustomer?.exempt_certificate && (() => {
                const cert = selectedCustomer.exempt_certificate!;
                const valid = cert.has_valid_certificate;
                const expiring = cert.status === 'expiring_soon';
                const bg = valid ? '#ECFDF5' : expiring ? '#FFFBEB' : '#FEF2F2';
                const border = valid ? '#10B981' : expiring ? '#F59E0B' : '#EF4444';
                const color = valid ? '#047857' : expiring ? '#B45309' : '#B91C1C';
                const label = valid
                  ? `✓ Tax-exempt certificate${cert.certificate_number ? ` #${cert.certificate_number}` : ''} — valid through ${cert.expiration_date}`
                  : expiring
                    ? `⚠ Certificate expiring ${cert.expiration_date} — verify before exempting`
                    : `⚠ Certificate EXPIRED ${cert.expiration_date} — NOT valid`;
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    margin: '6px 0', padding: '6px 12px',
                    background: bg, border: `1px solid ${border}`,
                    borderRadius: 8, fontSize: 13, fontWeight: 600, color,
                  }}>
                    <span>{label}</span>
                    <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>
                      (informational — cashier decides)
                    </span>
                  </div>
                );
              })()}

              {/* Invoice collection (A1): deliberate secondary step — the
                  panel lists the member's open invoices via their linked
                  billing Customer; unlinked members get a clear notice. */}
              {transaction && selectedCustomer && selectedCustomer.id != null && !selectedCustomer.is_unmatched && (
                <button
                  onClick={() => setInvoicePanelTarget({
                    memberId: selectedCustomer.id as number,
                    name: selectedCustomer.name,
                  })}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    margin: '6px 0',
                    padding: '8px 14px',
                    background: '#EFF6FF',
                    border: '1px solid #3B82F6',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1D4ED8',
                  }}
                >
                  💳 Open Invoices / Pay Balance
                </button>
              )}

              {/* Customer Bins Preview + Purchase History pill — show in
                  checkout view. Pill opens a side drawer with the usual
                  basket, recent baskets (POS + online), churn flag, and
                  for walk-ins the shadow-points "if they joined today"
                  invite hook. */}
              {selectedCustomer && (customerBins.length > 0 || purchaseHistoryKey) && (
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
                  {customerBins.length > 0 && (
                    <div style={styles.binsPreview}>
                      <span style={styles.binsPreviewLabel}>Feed Bins:</span>
                      {customerBins.map(bin => (
                        <span key={bin.id} style={styles.binPreviewItem}>
                          {bin.name} ({bin.feed_category_display}) - {bin.current_lbs.toFixed(0)} lbs
                        </span>
                      ))}
                    </div>
                  )}
                  {purchaseHistoryKey && (
                    <button
                      onClick={openPurchaseDrawer}
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: '#FFFFFF',
                        border: '1px solid #2D5A27',
                        color: '#2D5A27',
                        padding: '6px 12px',
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      🧾 Purchase History
                      {/* Churn-flag amber dot — if we've cached the data and
                          it has churn_flag=true, surface the warning so the
                          cashier knows this customer's been gone a while. */}
                      {purchaseHistory?.churn_flag && (
                        <span style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#F59E0B',
                          boxShadow: '0 0 0 2px #FEF3C7',
                        }} />
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Local Bridge Club points panel — only for local_access
                  members. Shows balance + apply-reward button when
                  redeemable. Earnings auto-fire after payment. */}
              {selectedCustomer?.local_points && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #86EFAC',
                  borderRadius: 8,
                  padding: 10,
                  margin: '8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#166534' }}>
                      {selectedCustomer.local_points.balance} pts
                      {selectedCustomer.local_points.redeemable_rewards > 0 ? (
                        <span style={{ marginLeft: 8, color: '#15803D' }}>
                          • {selectedCustomer.local_points.redeemable_rewards} reward{selectedCustomer.local_points.redeemable_rewards === 1 ? '' : 's'} available
                          (${selectedCustomer.local_points.redeemable_rewards * 10})
                        </span>
                      ) : (
                        <span style={{ marginLeft: 8, color: '#4B5563', fontWeight: 400 }}>
                          • {selectedCustomer.local_points.points_to_next_reward} pts to next $10 reward
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedCustomer.local_points.redeemable_rewards > 0 && (
                    <button
                      onClick={handleApplyLocalReward}
                      disabled={redeemingPoints}
                      style={{
                        background: '#2D5A27',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        cursor: redeemingPoints ? 'wait' : 'pointer',
                        opacity: redeemingPoints ? 0.7 : 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {redeemingPoints ? 'Applying…' : 'Apply $10 Reward'}
                    </button>
                  )}
                </div>
              )}

              {redeemMessage && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #86EFAC',
                  borderRadius: 8,
                  padding: 8,
                  margin: '4px 0',
                  color: '#166534',
                  fontSize: 13,
                }}>
                  {redeemMessage}
                </div>
              )}

              {/* Walk-in → existing member link confirmation. */}
              {linkMessage && (
                <div style={{
                  background: '#EFF6FF',
                  border: '1px solid #93C5FD',
                  borderRadius: 8,
                  padding: 8,
                  margin: '4px 0',
                  color: '#1E40AF',
                  fontSize: 13,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <span>{linkMessage}</span>
                  <button
                    onClick={() => setLinkMessage(null)}
                    style={{ background: 'none', border: 'none', color: '#1E40AF', cursor: 'pointer', fontSize: 14 }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Walk-in invite banner. Surfaces when an UnmatchedVisitor
                  is the selected customer and we haven't already sent
                  the invite this session. Goal: turn the visit-history
                  signal we already have into a one-tap recruit. */}
              {selectedCustomer?.is_unmatched && !inviteSent && (
                <div style={{
                  background: '#FFFBEB',
                  border: '1px solid #F59E0B',
                  borderRadius: 8,
                  padding: 12,
                  margin: '8px 0',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Invite to Bridge Club?
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 8, color: '#78350F' }}>
                    {selectedCustomer.name} has visited{' '}
                    {selectedCustomer.visit_count ?? 0}{' '}
                    time{selectedCustomer.visit_count === 1 ? '' : 's'} and spent ${selectedCustomer.total_spend ?? '0'}.
                    Joining loads their past purchase history.
                  </div>
                  {!showInviteInput ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setShowInviteInput(true)}
                        style={{
                          background: '#2D5A27', color: 'white',
                          border: 'none', borderRadius: 6,
                          padding: '6px 12px', cursor: 'pointer',
                        }}
                      >
                        Send Invite
                      </button>
                      {/* Link to an existing member — for walk-ins whose card
                          belongs to someone already in Bridge Club (e.g. a
                          spouse). Opens the customer search in link-mode. */}
                      <button
                        onClick={() => {
                          if (!selectedCustomer?.unmatched_visitor_id) return;
                          setLinkWalkinVisitorId(selectedCustomer.unmatched_visitor_id);
                          setCustomerSearchQuery('');
                          setCustomerSearchResults([]);
                          setShowCustomerModal(true);
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid #2563EB',
                          borderRadius: 6, padding: '6px 12px',
                          cursor: 'pointer', color: '#1D4ED8',
                        }}
                      >
                        Link to member
                      </button>
                      <button
                        onClick={() => {
                          setRedeemMessage(null);
                          // Route through the server detach so any redeemed
                          // points are refunded + the credit dropped; falls
                          // back to a local clear when there's no live txn.
                          if (transaction) {
                            handleAttachCustomer(null);
                          } else {
                            setSelectedCustomer(null);
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid #D97706',
                          borderRadius: 6, padding: '6px 12px',
                          cursor: 'pointer', color: '#92400E',
                        }}
                      >
                        Skip
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        style={{
                          flex: 1, padding: '6px 10px',
                          borderRadius: 6, border: '1px solid #D97706',
                        }}
                      />
                      <button
                        onClick={handleSendInvite}
                        disabled={inviteSubmitting}
                        style={{
                          background: '#2D5A27', color: 'white',
                          border: 'none', borderRadius: 6,
                          padding: '6px 12px',
                          cursor: inviteSubmitting ? 'wait' : 'pointer',
                          opacity: inviteSubmitting ? 0.7 : 1,
                        }}
                      >
                        {inviteSubmitting ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {inviteSent && (
                <div style={{
                  background: '#F0FDF4', border: '1px solid #86EFAC',
                  borderRadius: 8, padding: 10, margin: '8px 0',
                  color: '#166534',
                }}>
                  Invite sent! They'll get an email to create their account
                  with their purchase history pre-loaded.
                </div>
              )}

              {!transaction ? (
                <div style={styles.emptyCart}>
                  <p style={styles.emptyCartText}>No active transaction</p>
                  <button onClick={handleNewTransaction} style={styles.newSaleBtn}>
                    NEW SALE
                  </button>
                </div>
              ) : (
                <>
                  {/* Wholesale Will-Call banner — shown when this txn was
                      pre-staged from a wholesale SalesOrder. Surfaces
                      the order number, customer name, tax-exempt status,
                      and a status warning when the source order isn't
                      yet at READY_TO_SHIP. */}
                  {transaction.source_wholesale_order_id && (
                    <div style={{
                      background: '#ECFDF5',
                      border: '1px solid #10B981',
                      borderRadius: 8,
                      padding: '10px 12px',
                      margin: '0 0 8px 0',
                      fontSize: 13,
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 8,
                      }}>
                        <strong style={{ color: '#065F46' }}>
                          📦 Loaded from Wholesale Order
                          {' '}#{transaction.source_wholesale_order_number}
                        </strong>
                        {transaction.tax_exempt && (
                          <span style={{
                            fontSize: 11,
                            color: '#7C2D12',
                            fontWeight: 700,
                            background: '#FED7AA',
                            padding: '2px 6px',
                            borderRadius: 4,
                          }}>
                            TAX EXEMPT
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: 4, color: '#065F46' }}>
                        Customer: <strong>{transaction.attached_customer_name || transaction.customer_name}</strong>
                      </div>
                      {transaction.source_wholesale_order_status
                        && !['READY_TO_SHIP', 'READY'].includes(transaction.source_wholesale_order_status)
                        && (
                        <div style={{
                          marginTop: 6,
                          padding: '4px 8px',
                          background: '#FEF3C7',
                          color: '#92400E',
                          borderRadius: 4,
                          fontSize: 12,
                        }}>
                          ⚠ Order status is <strong>{transaction.source_wholesale_order_status}</strong> —
                          verify items are physically ready before pickup.
                        </div>
                      )}
                    </div>
                  )}
                  {/* Cart Items - Scrollable */}
                  <div style={styles.cartItems}>
                    {transaction.items.length === 0 ? (
                      <p style={styles.emptyCartText}>Scan items to add...</p>
                    ) : (
                      transaction.items.map((item) => {
                        // Invoice-payment lines (A1): fixed-amount balance
                        // collection — no qty/discount controls, distinct
                        // badge, removal via the invoice-payment endpoint.
                        const isInvoiceLine = (item.sku_snapshot || '').startsWith('INVPAY-');
                        return (
                        <div key={item.id} style={{
                          ...styles.cartItem,
                          ...(item.is_wholesale_line ? {
                            borderLeft: '3px solid #0E7490',
                            paddingLeft: 8,
                            background: '#F0FDFA',
                          } : {}),
                          ...(isInvoiceLine ? {
                            borderLeft: '3px solid #3B82F6',
                            paddingLeft: 8,
                            background: '#EFF6FF',
                          } : {}),
                          // Bag-credit pull: green-tinted line so a $0 pull reads
                          // instantly as bag credit, not a tax-exempt/errored $0.
                          ...(item.is_donation ? {
                            borderLeft: '3px solid #16A34A',
                            paddingLeft: 8,
                            background: '#F0FDF4',
                          } : {}),
                        }}>
                          {/* Product name and price on same line */}
                          <div style={styles.cartItemRow}>
                            <span style={styles.cartItemName}>
                              {item.product_name}
                              {item.is_weight_based && (
                                <span style={styles.cartItemUnit}> ({parseFloat(item.quantity).toFixed(2)} lb)</span>
                              )}
                              {item.sold_weight_lbs && (
                                <span style={styles.cartItemUnit}> — {parseFloat(item.sold_weight_lbs).toFixed(2)} lb @ ${parseFloat(item.unit_price).toFixed(2)}/lb</span>
                              )}
                              {isInvoiceLine && (
                                <span style={{
                                  fontSize: 10,
                                  color: '#1D4ED8',
                                  fontWeight: 700,
                                  background: '#DBEAFE',
                                  padding: '1px 5px',
                                  borderRadius: 3,
                                  marginLeft: 6,
                                }}>
                                  INVOICE PAYMENT
                                </span>
                              )}
                              {item.is_tax_exempt && !isInvoiceLine && (
                                <span style={styles.taxExemptBadge}>TAX EXEMPT</span>
                              )}
                              {item.is_wholesale_line && (
                                <span style={{
                                  fontSize: 10,
                                  color: '#0E7490',
                                  fontWeight: 700,
                                  background: '#CFFAFE',
                                  padding: '1px 5px',
                                  borderRadius: 3,
                                  marginLeft: 6,
                                }}>
                                  WHOLESALE
                                </span>
                              )}
                              <BagCreditBadge item={item} />
                            </span>
                            <span style={styles.cartItemTotal}>
                              {parseFloat(item.discount_amount || '0') > 0 ? (
                                <>
                                  <span style={styles.originalPrice}>
                                    ${(parseFloat(item.unit_price) * parseFloat(item.quantity)).toFixed(2)}
                                  </span>
                                  {' '}${parseFloat(item.line_total).toFixed(2)}
                                </>
                              ) : (
                                <>${parseFloat(item.line_total).toFixed(2)}</>
                              )}
                            </span>
                          </div>
                          {/* Discount info line */}
                          {parseFloat(item.discount_amount || '0') > 0 && (
                            <div style={styles.discountInfoRow}>
                              <span style={styles.discountInfoText}>
                                Discount: {item.discount_type === 'percent' ? `${parseFloat(item.discount_value || '0')}%` : `$${parseFloat(item.discount_value || '0').toFixed(2)}`} (-${parseFloat(item.discount_amount || '0').toFixed(2)})
                              </span>
                              <button
                                onClick={() => handleRemoveItemDiscount(item.id)}
                                style={styles.discountRemoveBtn}
                                title="Remove discount"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                          {/* Bag-credit pull: persistent on-line explanation +
                              decline, using the same label the receipt prints. */}
                          <CartBagCreditLine item={item} onDecline={handleDeclineBagPull} />
                          {/* Quantity controls on second line */}
                          {isInvoiceLine ? (
                            <>
                            {/* Read-only invoice contents — context for the
                                cashier; the $ collected stays the applied
                                payment amount above, not these lines. */}
                            {(item.invoice_payment?.line_items?.length ?? 0) > 0 && (
                              <div style={{
                                margin: '2px 0 4px 10px',
                                paddingLeft: 8,
                                borderLeft: '2px solid #BFDBFE',
                              }}>
                                {item.invoice_payment!.line_items.map((li, liIdx) => (
                                  <div key={liIdx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    fontSize: 12,
                                    color: '#4B5563',
                                  }}>
                                    <span>
                                      {li.name}
                                      {(li.quantity_lbs > 0 || li.packages > 0) && (
                                        <span style={{ color: '#6B7280' }}>
                                          {' — '}
                                          {li.quantity_lbs > 0 ? `${li.quantity_lbs.toLocaleString()} lbs` : ''}
                                          {li.quantity_lbs > 0 && li.packages > 0 ? ' / ' : ''}
                                          {li.packages > 0 ? `${li.packages} ${li.packages === 1 ? 'bag' : 'bags'}` : ''}
                                        </span>
                                      )}
                                    </span>
                                    <span style={{ whiteSpace: 'nowrap' }}>${li.line_total.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={styles.cartItemControls}>
                              <span style={{ fontSize: 12, color: '#1D4ED8', flex: 1 }}>
                                Applies to invoice balance at checkout
                              </span>
                              <button
                                onClick={handleRemoveInvoicePayment}
                                style={styles.removeBtn}
                                title="Remove invoice payment"
                              >
                                ✕
                              </button>
                            </div>
                            </>
                          ) : (
                          <div style={styles.cartItemControls}>
                            <CartItemQuantityControl
                              item={item}
                              onUpdateQuantity={handleUpdateQuantity}
                              onRemove={handleRemoveItem}
                              styles={styles}
                            />
                            {/* Discount button */}
                            <button
                              onClick={() => openItemDiscount(item.id)}
                              style={styles.itemDiscountBtn}
                              title="Apply discount"
                            >
                              %
                            </button>
                            {/* Per-line exempt toggle (Build B). Walk-in
                                product lines only (product-less INVPAY/
                                wholesale lines are fixed at load time).
                                Decoupled from eligibility — cashier decides. */}
                            {item.product != null && (
                              <button
                                onClick={() => handleSetLineTaxExempt(item.id, !item.is_tax_exempt)}
                                style={{
                                  border: `1px solid ${item.is_tax_exempt ? '#10B981' : '#D1D5DB'}`,
                                  background: item.is_tax_exempt ? '#10B981' : '#FFF',
                                  color: item.is_tax_exempt ? '#FFF' : '#6B7280',
                                  borderRadius: 6,
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                                title={item.is_tax_exempt
                                  ? 'Tax exempt — tap to charge tax'
                                  : 'Taxed — tap to exempt this line'}
                              >
                                {item.is_tax_exempt ? 'EXEMPT' : 'TAX'}
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              style={styles.removeBtn}
                              title="Remove item"
                            >
                              ✕
                            </button>
                          </div>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
              </div>{/* end cartScrollRegion */}

              {/* Cart Footer - anchored at the bottom of cartArea, outside
                  the scroll region so it never scrolls off-screen. */}
              {transaction && (
                  <div style={styles.cartFooter}>
                    {/* Parked carts (per-cashier): recall or discard from the tray. */}
                    <ParkedCartsTray
                      carts={parkedCarts}
                      onRecall={handleRecallCart}
                      onDiscard={handleDiscardCart}
                    />
                    {/* Customer tab (Phase 4): balance/limit/room + pay-down /
                        charge-to-tab, so the cashier sees the tab before charging. */}
                    <TabBanner
                      tab={transaction.tab}
                      cartRemaining={parseFloat(String(transaction.total || '0'))}
                      onPayDown={handlePayDownTab}
                      onChargeToTab={handleChargeToTab}
                    />
                    {/* Tax Exempt & Cart Discount + Park buttons */}
                    {transaction.items.length > 0 && (
                      <div style={styles.cartToolbar}>
                        {transaction.items.some(i => i.tax_exempt_eligible) && (
                          <button
                            onClick={handleToggleTaxExempt}
                            style={transaction.tax_exempt ? styles.taxExemptBtnActive : styles.taxExemptBtn}
                          >
                            {transaction.tax_exempt ? 'TAX EXEMPT ON' : 'TAX EXEMPT'}
                          </button>
                        )}
                        <button
                          onClick={openCartDiscount}
                          style={styles.cartDiscountBtn}
                        >
                          CART %
                        </button>
                        <button
                          onClick={handleParkSale}
                          style={styles.cartDiscountBtn}
                          title="Park this sale aside and start a fresh cart"
                        >
                          PARK
                        </button>
                      </div>
                    )}

                    {/* Totals */}
                    <div style={styles.cartTotals}>
                      <div style={styles.totalRow}>
                        <span>Subtotal:</span>
                        <span>${parseFloat(transaction.subtotal).toFixed(2)}</span>
                      </div>
                      <div style={styles.totalRow}>
                        <span>Tax:</span>
                        <span>${parseFloat(transaction.tax_amount).toFixed(2)}</span>
                      </div>
                      {parseFloat(transaction.discount_amount) > 0 && (
                        <div style={styles.totalRow}>
                          <span style={styles.discountLabel}>
                            Discount{transaction.cart_discount_type ? ` (${transaction.cart_discount_type === 'percent' ? parseFloat(transaction.cart_discount_value || '0') + '%' : '$' + parseFloat(transaction.cart_discount_value || '0').toFixed(2)})` : ''}:
                          </span>
                          <span style={styles.discountLabel}>
                            -${parseFloat(transaction.discount_amount).toFixed(2)}
                            {transaction.cart_discount_type && (
                              <button
                                onClick={handleRemoveCartDiscount}
                                style={styles.discountRemoveBtnSmall}
                                title="Remove cart discount"
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        </div>
                      )}
                      {parseFloat(transaction.local_points_redeemed_amount || '0') > 0 && (
                        <div style={styles.totalRow}>
                          <span style={styles.discountLabel}>Reward credit:</span>
                          <span style={styles.discountLabel}>
                            -${parseFloat(transaction.local_points_redeemed_amount || '0').toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div style={styles.grandTotalRow}>
                        <span>TOTAL:</span>
                        <span>${parseFloat(transaction.total).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={styles.cartActions}>
                      <button
                        onClick={handleVoidTransaction}
                        style={styles.voidBtn}
                      >
                        VOID
                      </button>
                      <button
                        onClick={handlePayment}
                        disabled={transaction.items.length === 0}
                        style={{
                          ...styles.payBtn,
                          opacity: transaction.items.length === 0 ? 0.5 : 1,
                        }}
                      >
                        PAY ${parseFloat(transaction.total).toFixed(2)}
                      </button>
                    </div>
                  </div>
              )}
            </div>
          </div>
        )}

        {/* Search View */}
        {viewMode === 'search' && (
          <div style={styles.searchView}>
            <div style={styles.searchHeader}>
              <button onClick={() => setViewMode('checkout')} style={styles.backBtn}>
                BACK
              </button>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                style={styles.searchInput}
                autoFocus
              />
            </div>

            {/* Category Filter */}
            <div style={styles.categoryFilter}>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  ...styles.categoryBtn,
                  backgroundColor: selectedCategory === null ? '#1a4d1a' : '#e0e0e0',
                  color: selectedCategory === null ? '#fff' : '#333',
                }}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  style={{
                    ...styles.categoryBtn,
                    backgroundColor: selectedCategory === cat.id ? '#1a4d1a' : '#e0e0e0',
                    color: selectedCategory === cat.id ? '#fff' : '#333',
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Search Results */}
            <div style={styles.searchResults}>
              {loading ? (
                <div style={styles.loadingText}>Searching...</div>
              ) : searchResults.length === 0 ? (
                <div style={styles.noResults}>No products found</div>
              ) : (
                searchResults.map((product) => {
                  const colors = getCategoryColor(product.category_name);
                  return (
                    <button
                      key={product.id}
                      onClick={() => {
                        addProductToCart(product);
                        setViewMode('checkout');
                      }}
                      style={{
                        ...styles.searchResultItem,
                        backgroundColor: colors.bg,
                        borderLeftColor: colors.bg,
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.hover;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.bg;
                      }}
                    >
                      <div style={styles.resultInfo}>
                        <span style={{ ...styles.resultName, color: colors.text }}>{product.name}</span>
                        <span style={{ ...styles.resultSku, color: colors.text, opacity: 0.8 }}>{product.sku}</span>
                      </div>
                      <div style={{ ...styles.resultPrice, color: colors.text }}>
                        ${parseFloat(product.effective_price || product.retail_price).toFixed(2)}
                        {product.is_byoc && <span style={{ ...styles.byocTag, color: colors.text }}>/lb</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Payment View */}
        {viewMode === 'payment' && transaction && (
          <div style={styles.paymentView}>
            <div style={styles.paymentHeader}>
              <button onClick={() => setViewMode('checkout')} style={styles.backBtn}>
                BACK
              </button>
              <h2 style={styles.paymentTitle}>
                Total Due: ${parseFloat(transaction.total).toFixed(2)}
              </h2>
            </div>

            {/* Receipt Options */}
            <div style={styles.receiptOptions}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={printReceipt_}
                  onChange={(e) => setPrintReceipt_(e.target.checked)}
                  style={styles.checkbox}
                />
                Print Receipt
              </label>
              {selectedCustomer && (
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={emailReceipt}
                    onChange={(e) => setEmailReceipt(e.target.checked)}
                    style={styles.checkbox}
                  />
                  Email Receipt
                </label>
              )}
            </div>

            {/* Bin Loading Section - only show if customer has bins */}
            {selectedCustomer && binLoadSelections.length > 0 && (
              <div style={styles.binLoadSection}>
                <div style={styles.binLoadHeader}>
                  <span style={styles.binLoadTitle}>LOAD BINS ({selectedCustomer.name})</span>
                </div>
                <div style={styles.binLoadList}>
                  {binLoadSelections.map((selection, index) => (
                    <div key={selection.lineKey} style={styles.binLoadItem}>
                      <span style={styles.binName}>{selection.productName}</span>
                      <span style={styles.binLbs}>{selection.quantity} lbs →</span>
                      <select
                        value={selection.binId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          const updated = [...binLoadSelections];
                          updated[index] = { ...updated[index], binId: v ? parseInt(v, 10) : null };
                          setBinLoadSelections(updated);
                        }}
                        style={styles.binQuantityInput}
                      >
                        <option value="">— Don't track —</option>
                        {customerBins.map(b => (
                          <option key={b.id} value={b.id}>
                            {b.name} ({b.feed_category_display})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Split-payment toggle + remaining-balance banner.
                The backend has always supported multiple POSPayment rows per
                transaction — this UI lets the cashier mix cash/check/card on
                one sale by paying a slice at a time. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button
                onClick={() => {
                  setSplitMode(prev => {
                    const next = !prev;
                    if (!next) {
                      // Leaving split mode — clear the slice input.
                      setSplitAmountInput('');
                    }
                    return next;
                  });
                  setError('');
                }}
                style={{
                  background: splitMode ? '#1a4d1a' : 'transparent',
                  border: '1px solid #6B7280',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: splitMode ? '#fff' : '#374151',
                  fontWeight: 600,
                }}
              >
                {splitMode ? '✓ SPLIT PAYMENT' : 'SPLIT PAYMENT'}
              </button>
            </div>

            {splitMode && (
              <div style={{
                background: '#F0FDF4',
                border: '1px solid #86EFAC',
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#14532D' }}>
                  Remaining: ${amountDue.toFixed(2)}
                </div>
                {splitPayments.length > 0 && (
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>
                    {splitPayments.map((p) => (
                      <div key={p.id}>
                        {p.methodDisplay}
                        {p.cardLastFour ? ` •••• ${p.cardLastFour}` : ''}
                        {p.checkNumber ? ` #${p.checkNumber}` : ''}
                        : ${p.amount.toFixed(2)}
                        {p.healedFromEmail && (
                          <span style={{
                            marginLeft: 6, color: '#92400E',
                            background: '#FEF3C7', borderRadius: 3,
                            padding: '0 4px', fontSize: 10, fontWeight: 700,
                          }}>healed</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payment Method Tabs */}
            <div style={styles.paymentTabs}>
              <button
                onClick={() => setPaymentMethod('cash')}
                style={{
                  ...styles.paymentTab,
                  backgroundColor: paymentMethod === 'cash' ? '#1a4d1a' : '#e0e0e0',
                  color: paymentMethod === 'cash' ? '#fff' : '#333',
                }}
              >
                CASH
              </button>
              <button
                onClick={() => setPaymentMethod('check')}
                style={{
                  ...styles.paymentTab,
                  backgroundColor: paymentMethod === 'check' ? '#1a4d1a' : '#e0e0e0',
                  color: paymentMethod === 'check' ? '#fff' : '#333',
                }}
              >
                CHECK
              </button>
              <button
                onClick={() => setPaymentMethod('card')}
                style={{
                  ...styles.paymentTab,
                  backgroundColor: paymentMethod === 'card' ? '#1a4d1a' : '#e0e0e0',
                  color: paymentMethod === 'card' ? '#fff' : '#333',
                }}
              >
                CARD
              </button>
            </div>

            {/* Cash Payment */}
            {paymentMethod === 'cash' && (
              <div style={styles.cashPayment}>
                <div style={styles.cashInputSection}>
                  <label style={styles.inputLabel}>Amount Tendered:</label>
                  <input
                    type="number"
                    value={cashTendered}
                    onChange={(e) => setCashTendered(e.target.value)}
                    placeholder="0.00"
                    style={styles.cashInput}
                    step="0.01"
                    autoFocus
                  />
                </div>

                {/* Split-payment slice input — what amount of the remaining
                    balance to apply this cash to. Leave blank to default to
                    min(tendered, remaining). */}
                {splitMode && (
                  <div style={styles.cashInputSection}>
                    <label style={styles.inputLabel}>
                      Apply to balance (optional, max ${amountDue.toFixed(2)}):
                    </label>
                    <input
                      type="number"
                      value={splitAmountInput}
                      onChange={(e) => setSplitAmountInput(e.target.value)}
                      placeholder={`${amountDue.toFixed(2)} (default)`}
                      style={styles.cashInput}
                      step="0.01"
                      max={amountDue}
                    />
                  </div>
                )}

                {/* Quick Cash Buttons */}
                <div style={styles.quickCashGrid}>
                  {quickCashAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleQuickCash(amount)}
                      style={styles.quickCashBtn}
                    >
                      +${amount}
                    </button>
                  ))}
                  <button
                    onClick={() => setCashTendered(
                      (splitMode ? amountDue : parseFloat(transaction.total)).toFixed(2)
                    )}
                    style={styles.exactBtn}
                  >
                    EXACT
                  </button>
                  <button
                    onClick={() => setCashTendered('')}
                    style={styles.clearBtn}
                  >
                    CLEAR
                  </button>
                </div>

                {/* Change Display — uses split slice when in split mode. */}
                {(() => {
                  const tendered = parseFloat(cashTendered || '0');
                  const slice = splitMode
                    ? (splitAmountInput
                        ? parseFloat(splitAmountInput)
                        : Math.min(tendered, amountDue))
                    : parseFloat(transaction.total);
                  if (cashTendered && tendered >= slice && slice > 0) {
                    return (
                      <div style={styles.changeDisplay}>
                        Change: ${(tendered - slice).toFixed(2)}
                      </div>
                    );
                  }
                  return null;
                })()}

                {(() => {
                  const tendered = parseFloat(cashTendered || '0');
                  const minRequired = splitMode
                    ? (splitAmountInput
                        ? parseFloat(splitAmountInput)
                        : Math.min(tendered, amountDue))
                    : parseFloat(transaction.total);
                  const disabled = loading || !cashTendered || tendered <= 0
                    || (splitMode ? tendered < minRequired || minRequired <= 0
                                  : tendered < parseFloat(transaction.total));
                  const label = splitMode
                    ? (amountDue > minRequired ? 'APPLY PAYMENT' : 'COMPLETE SALE')
                    : 'COMPLETE SALE';
                  return (
                    <button
                      onClick={() => handleCashPayment()}
                      disabled={disabled}
                      style={{
                        ...styles.completePaymentBtn,
                        opacity: disabled ? 0.5 : 1,
                      }}
                    >
                      {loading ? 'Processing...' : label}
                    </button>
                  );
                })()}
              </div>
            )}

            {/* Check Payment */}
            {paymentMethod === 'check' && (
              <div style={styles.cashPayment}>
                <div style={styles.cashInputSection}>
                  <label style={styles.inputLabel}>Check Number:</label>
                  <input
                    type="text"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="Enter check number"
                    style={styles.cashInput}
                    autoFocus
                  />
                </div>

                {/* Split-payment slice input — defaults to the full remaining balance. */}
                {splitMode && (
                  <div style={styles.cashInputSection}>
                    <label style={styles.inputLabel}>
                      Check amount (max ${amountDue.toFixed(2)}):
                    </label>
                    <input
                      type="number"
                      value={splitAmountInput}
                      onChange={(e) => setSplitAmountInput(e.target.value)}
                      placeholder={`${amountDue.toFixed(2)} (default)`}
                      style={styles.cashInput}
                      step="0.01"
                      max={amountDue}
                    />
                  </div>
                )}

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  textAlign: 'center',
                  fontSize: '1.25rem',
                }}>
                  Amount: <strong>${
                    splitMode
                      ? (splitAmountInput ? parseFloat(splitAmountInput) : amountDue).toFixed(2)
                      : (transaction ? parseFloat(transaction.total).toFixed(2) : '0.00')
                  }</strong>
                </div>

                <button
                  onClick={() => handleCheckPayment()}
                  disabled={loading || !checkNumber.trim()}
                  style={{
                    ...styles.completePaymentBtn,
                    opacity: loading || !checkNumber.trim() ? 0.5 : 1,
                  }}
                >
                  {loading ? 'Processing...' : (splitMode && amountDue > (parseFloat(splitAmountInput) || amountDue) ? 'APPLY PAYMENT' : 'COMPLETE SALE')}
                </button>
              </div>
            )}

            {/* Card Payment */}
            {paymentMethod === 'card' && (
              <div style={styles.cardPayment}>
                {/* Split-payment slice input — defaults to remaining balance. */}
                {splitMode && !cardPaymentStatus && (
                  <div style={styles.cashInputSection}>
                    <label style={styles.inputLabel}>
                      Charge to card (max ${amountDue.toFixed(2)}):
                    </label>
                    <input
                      type="number"
                      value={splitAmountInput}
                      onChange={(e) => setSplitAmountInput(e.target.value)}
                      placeholder={`${amountDue.toFixed(2)} (default)`}
                      style={styles.cashInput}
                      step="0.01"
                      max={amountDue}
                    />
                  </div>
                )}

                {/* Declined panel — replaces the silent error-banner + reset-to-
                    ACTIVATE behavior. Shows the verbatim Helcim message, lets
                    the cashier retry or jump straight to cash, and after >1
                    decline warns them to consider another method. */}
                {cardPaymentStatus === 'declined' && (
                  <div style={{
                    background: '#FEF2F2',
                    border: '1px solid #FCA5A5',
                    borderRadius: 8,
                    padding: 16,
                    textAlign: 'center',
                    marginBottom: 12,
                  }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>❌</div>
                    <div style={{ fontWeight: 700, color: '#DC2626', fontSize: 16 }}>
                      Payment Declined
                    </div>
                    <div style={{ color: '#7F1D1D', fontSize: 13, margin: '8px 0' }}>
                      {lastDeclineReason}
                    </div>
                    {cardDeclineCount > 1 && (
                      <div style={{
                        color: '#92400E', fontSize: 12,
                        background: '#FEF3C7', borderRadius: 4,
                        padding: '4px 8px', marginTop: 8,
                      }}>
                        {cardDeclineCount} attempts — consider trying a different
                        payment method
                      </div>
                    )}
                    <div style={{
                      display: 'flex', gap: 8,
                      justifyContent: 'center', marginTop: 12,
                    }}>
                      <button
                        onClick={() => {
                          setCardPaymentStatus('');
                          setError('');
                        }}
                        style={{
                          background: '#2D5A27', color: 'white',
                          border: 'none', borderRadius: 6,
                          padding: '8px 16px', cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        🔄 Retry Card
                      </button>
                      <button
                        onClick={() => {
                          setCardPaymentStatus('');
                          setError('');
                          setPaymentMethod('cash');
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid #D97706',
                          borderRadius: 6, padding: '8px 16px',
                          cursor: 'pointer', color: '#92400E',
                          fontWeight: 600,
                        }}
                      >
                        Use Cash Instead
                      </button>
                    </div>
                  </div>
                )}

                {!cardPaymentStatus ? (
                  <>
                    <p style={styles.cardInstructions}>
                      Press the button below to activate the card terminal.
                    </p>
                    <p style={styles.cardNote}>
                      Credit cards: 3% fee applies<br />
                      Debit cards: No fee
                    </p>
                    <button
                      onClick={() => handleCardPayment()}
                      disabled={loading}
                      style={styles.activateTerminalBtn}
                    >
                      {loading ? 'Initializing...' : 'ACTIVATE TERMINAL'}
                    </button>
                  </>
                ) : cardPaymentApproved ? (
                  <div style={styles.cardWaiting}>
                    <div style={styles.cardApprovedIcon}>&#10003;</div>
                    <p style={styles.cardApprovedText}>Payment Approved!</p>
                    {cardIsCredit !== null && (
                      <p style={styles.cardTypeLabel}>
                        {cardIsCredit ? 'Credit Card' : 'Debit Card'}
                        {cardIsCredit && ' (3% fee applied)'}
                      </p>
                    )}
                    <p style={styles.cardTransitioning}>Loading receipt...</p>
                    <button
                      onClick={handleNewSale}
                      style={styles.newSaleBtn}
                    >
                      NEW SALE
                    </button>
                  </div>
                ) : cardPaymentStatus === 'declined' ? (
                  /* Declined panel already rendered above the chain. */
                  null
                ) : (
                  <div style={styles.cardWaiting}>
                    <div style={styles.spinnerLarge} />
                    <p style={styles.cardStatus}>{cardPaymentStatus}</p>
                    <button
                      onClick={handleCancelCardPayment}
                      style={styles.cancelCardBtn}
                    >
                      CANCEL
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Receipt View */}
        {viewMode === 'receipt' && receipt && (
          <div style={styles.receiptView}>
            <div style={styles.receiptSuccess}>
              <div style={styles.checkmark}>&#10003;</div>
              <h2 style={styles.successTitle}>Sale Complete!</h2>
              {changeDue !== null && changeDue > 0 && (
                <div style={styles.changeAmount}>
                  Change Due: ${changeDue.toFixed(2)}
                </div>
              )}
            </div>

            <div style={styles.receiptPreview}>
              <div style={styles.receiptHeader}>
                <strong>{receipt.store_name}</strong><br />
                {receipt.date} {receipt.time}<br />
                Cashier: {receipt.cashier}
              </div>
              <div style={styles.receiptItems}>
                {receipt.items.map((item, i) => (
                  item.invoice_payment ? (
                    // Invoice-payment line: show the invoice number and
                    // the post-payment balance, mirroring the thermal print.
                    <div key={i}>
                      <div style={styles.receiptItem}>
                        <span style={{ fontWeight: 700 }}>
                          Invoice {item.invoice_payment.invoice_number}
                        </span>
                        <span>${item.total.toFixed(2)}</span>
                      </div>
                      {/* Invoice contents — read-only context showing what
                          the invoice covers (not what was charged now). */}
                      {(item.invoice_payment.line_items?.length ?? 0) > 0 && (
                        <div style={{ margin: '2px 0 2px 12px' }}>
                          {item.invoice_payment.line_items.map((li, liIdx) => (
                            <div key={liIdx} style={{ ...styles.receiptItem, fontSize: 12, color: '#6B7280' }}>
                              <span>
                                {li.name}
                                {(li.quantity_lbs > 0 || li.packages > 0) &&
                                  ` — ${li.quantity_lbs > 0 ? `${li.quantity_lbs.toLocaleString()} lbs` : ''}${li.quantity_lbs > 0 && li.packages > 0 ? ' / ' : ''}${li.packages > 0 ? `${li.packages} ${li.packages === 1 ? 'bag' : 'bags'}` : ''}`}
                              </span>
                              <span>${li.line_total.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ ...styles.receiptItem, fontSize: 13, color: '#374151' }}>
                        {item.invoice_payment.paid_in_full === true ? (
                          <span style={{ fontWeight: 700, color: '#16a34a' }}>✓ PAID IN FULL</span>
                        ) : item.invoice_payment.remaining_balance != null ? (
                          <>
                            <span>Remaining balance</span>
                            <span>${item.invoice_payment.remaining_balance.toFixed(2)}</span>
                          </>
                        ) : (
                          <span>Payment applied to invoice</span>
                        )}
                      </div>
                    </div>
                  ) : (
                  <div key={i}>
                    <div style={styles.receiptItem}>
                      <span>{item.name}</span>
                      <span>${item.total.toFixed(2)}</span>
                    </div>
                    {item.is_donation && item.bag_credit_label && (
                      <div style={{ ...styles.receiptItem, fontSize: 12, color: '#166534', marginLeft: 12 }}>
                        <span>{item.bag_credit_label}</span>
                        <span>$0.00</span>
                      </div>
                    )}
                  </div>
                  )
                ))}
              </div>
              <div style={styles.receiptTotals}>
                <div style={styles.receiptTotalLine}>
                  <span>Subtotal:</span>
                  <span>${receipt.subtotal.toFixed(2)}</span>
                </div>
                <div style={styles.receiptTotalLine}>
                  <span>Tax:</span>
                  <span>${receipt.tax.toFixed(2)}</span>
                </div>
                {receipt.surcharge > 0 && (
                  <div style={styles.receiptTotalLine}>
                    <span>Credit Card Fee (3%):</span>
                    <span>${receipt.surcharge.toFixed(2)}</span>
                  </div>
                )}
                <div style={styles.receiptGrandTotal}>
                  <span>TOTAL:</span>
                  <span>${receipt.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Bins Loaded Section */}
              {binLoadResults.length > 0 && (
                <div style={styles.receiptBinsSection}>
                  <div style={styles.receiptBinsHeader}>BINS LOADED</div>
                  {binLoadResults.map((bin, i) => (
                    <div key={i} style={styles.receiptBinItem}>
                      <span style={styles.receiptBinName}>{bin.binName}:</span>
                      <span style={styles.receiptBinAdded}>+{bin.addedLbs} lbs</span>
                      <span style={styles.receiptBinLevel}>→ {bin.newLevel} lbs ({bin.fillPct}% full)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {printError && (
              <div style={styles.printError}>{printError}</div>
            )}

            <div style={styles.receiptActions}>
              <button
                onClick={handlePrintReceipt}
                disabled={printing}
                style={{
                  ...styles.printBtn,
                  opacity: printing ? 0.6 : 1,
                }}
              >
                {printing ? 'PRINTING...' : 'PRINT RECEIPT'}
              </button>
              <button onClick={handleNewSale} style={styles.newSaleBtn}>
                NEW SALE
              </button>
            </div>
            <button
              onClick={handleOpenDrawer}
              style={styles.drawerBtn}
            >
              OPEN DRAWER
            </button>
          </div>
        )}

        {/* Refund View */}
        {viewMode === 'refund' && (
          <div style={styles.refundView}>
            {refundTapMessage ? (
              /* Card-present debit refund — waiting for the customer to tap */
              <div style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💳</div>
                <h2 style={{ margin: '0 0 0.5rem 0' }}>Waiting for Card…</h2>
                <p style={{ color: '#374151', fontSize: '1.05rem' }}>{refundTapMessage}</p>
                <p style={{ color: '#6b7280', marginTop: '1rem' }}>
                  The customer taps their original debit card at the reader to receive the refund.
                </p>
              </div>
            ) : !refundResult ? (
              <>
                {/* Phase 1: Lookup OR Phase 2: Review */}
                <div style={styles.refundHeader}>
                  <button onClick={handleRefundDone} style={styles.refundBackBtn}>
                    &#8592; BACK
                  </button>
                  <h2 style={styles.refundTitle}>Process Refund</h2>
                </div>

                {!refundLookupData ? (
                  /* Phase 1: Transaction Lookup */
                  <div style={styles.refundLookupSection}>
                    <label style={styles.refundLabel}>Enter Transaction Number:</label>
                    <div style={styles.refundLookupRow}>
                      <input
                        type="text"
                        value={refundTxnNumber}
                        onChange={(e) => setRefundTxnNumber(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRefundLookup(); }}
                        placeholder="e.g. 20260210-0001"
                        style={styles.refundInput}
                        autoFocus
                      />
                      <button
                        onClick={handleRefundLookup}
                        disabled={refundLookupLoading || !refundTxnNumber.trim()}
                        style={{
                          ...styles.refundLookupBtn,
                          opacity: refundLookupLoading || !refundTxnNumber.trim() ? 0.5 : 1,
                        }}
                      >
                        {refundLookupLoading ? 'LOOKING UP...' : 'LOOK UP'}
                      </button>
                    </div>
                    {refundLookupError && (
                      <div style={styles.refundError}>{refundLookupError}</div>
                    )}
                  </div>
                ) : (
                  /* Phase 2: Review & Options */
                  <div style={styles.refundReviewSection}>
                    {/* Transaction Summary */}
                    <div style={styles.refundTxnSummary}>
                      <div style={styles.refundTxnHeader}>
                        <span style={styles.refundTxnNumber}>#{refundLookupData.transaction.transaction_number}</span>
                        <span style={styles.refundTxnDate}>
                          {refundLookupData.transaction.date} {refundLookupData.transaction.time}
                        </span>
                      </div>
                      <div style={styles.refundTxnCashier}>
                        Cashier: {refundLookupData.transaction.cashier}
                      </div>
                    </div>

                    {/* Warnings */}
                    {refundLookupData.age_warning && (
                      <div style={styles.refundWarningBanner}>
                        This transaction is {refundLookupData.days_since_purchase} days old
                      </div>
                    )}
                    {refundLookupData.transaction.refund_status === 'partial' && (
                      <div style={styles.refundInfoBanner}>
                        Partially refunded — ${refundLookupData.transaction.total_refunded.toFixed(2)} already refunded.
                        Remaining: ${refundLookupData.refundable_amount.toFixed(2)}
                      </div>
                    )}

                    {/* Items */}
                    <div style={styles.refundItemsList}>
                      {refundLookupData.transaction.items.map((item: any) => (
                        <div
                          key={item.id}
                          style={{
                            ...styles.refundItem,
                            opacity: item.remaining_quantity <= 0 ? 0.4 : 1,
                            ...(refundType === 'partial' && refundSelectedItems.has(item.id) ? { backgroundColor: '#fff3e0', borderColor: '#ff9800' } : {}),
                          }}
                          onClick={() => {
                            if (refundType === 'partial' && item.remaining_quantity > 0) {
                              handleRefundToggleItem(item.id);
                            }
                          }}
                        >
                          <div style={styles.refundItemInfo}>
                            {refundType === 'partial' && item.remaining_quantity > 0 && (
                              <span style={styles.refundItemCheckbox}>
                                {refundSelectedItems.has(item.id) ? '\u2611' : '\u2610'}
                              </span>
                            )}
                            <span style={styles.refundItemName}>{item.product_name}</span>
                          </div>
                          <div style={styles.refundItemDetails}>
                            <span>{item.remaining_quantity > 0 ? `${item.remaining_quantity} ${item.is_weight_based ? 'lb' : 'ea'}` : 'REFUNDED'}</span>
                            <span style={styles.refundItemPrice}>${item.line_total.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Refund Type Toggle */}
                    <div style={styles.refundTypeToggle}>
                      <button
                        onClick={() => { setRefundType('full'); setRefundSelectedItems(new Set()); setRefundCustomAmount(''); }}
                        style={{
                          ...styles.refundTypeBtn,
                          ...(refundType === 'full' ? styles.refundTypeBtnActive : {}),
                        }}
                      >
                        FULL REFUND
                        <span style={styles.refundTypeAmount}>${refundLookupData.refundable_amount.toFixed(2)}</span>
                      </button>
                      <button
                        onClick={() => setRefundType('partial')}
                        style={{
                          ...styles.refundTypeBtn,
                          ...(refundType === 'partial' ? styles.refundTypeBtnActive : {}),
                        }}
                      >
                        PARTIAL REFUND
                        <span style={styles.refundTypeAmount}>Select items above</span>
                      </button>
                    </div>

                    {/* Custom amount for partial */}
                    {refundType === 'partial' && refundSelectedItems.size === 0 && (
                      <div style={styles.refundCustomAmountSection}>
                        <label style={styles.refundLabel}>Or enter custom refund amount:</label>
                        <input
                          type="number"
                          value={refundCustomAmount}
                          onChange={(e) => setRefundCustomAmount(e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          max={refundLookupData.refundable_amount}
                          style={styles.refundInput}
                        />
                      </div>
                    )}

                    {/* Reason */}
                    <div style={styles.refundReasonSection}>
                      <label style={styles.refundLabel}>Reason for refund (required):</label>
                      <input
                        type="text"
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="e.g. Wrong product, damaged goods, customer request..."
                        style={styles.refundInput}
                        maxLength={500}
                      />
                    </div>

                    {refundLookupError && (
                      <div style={styles.refundError}>{refundLookupError}</div>
                    )}

                    {/* Process Button */}
                    <button
                      onClick={handleProcessRefund}
                      disabled={refundProcessing || !refundReason.trim() || (refundType === 'partial' && refundSelectedItems.size === 0 && !refundCustomAmount)}
                      style={{
                        ...styles.refundProcessBtn,
                        opacity: refundProcessing || !refundReason.trim() || (refundType === 'partial' && refundSelectedItems.size === 0 && !refundCustomAmount) ? 0.5 : 1,
                      }}
                    >
                      {refundProcessing ? 'PROCESSING...' : 'PROCESS REFUND'}
                    </button>

                    <button
                      onClick={() => { setRefundLookupData(null); setRefundTxnNumber(''); setRefundLookupError(''); }}
                      style={styles.refundCancelBtn}
                    >
                      LOOK UP DIFFERENT TRANSACTION
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Phase 3: Result */
              <div style={styles.refundResultSection}>
                {refundResult.status === 'approved' ? (
                  <>
                    <div style={styles.refundSuccessIcon}>&#10003;</div>
                    {refundResult.action_required === 'return_cash' ? (
                      <>
                        <h2 style={styles.refundResultTitle}>Return Cash to Customer</h2>
                        <div style={styles.refundCashAmount}>${refundResult.total.toFixed(2)}</div>
                      </>
                    ) : (
                      <>
                        <h2 style={styles.refundResultTitle}>Refund Complete</h2>
                        <div style={styles.refundResultAmount}>${refundResult.total.toFixed(2)}</div>
                        {refundResult.card_type && refundResult.card_last_four && (
                          <div style={styles.refundResultCard}>
                            Refunded to {refundResult.card_type} ****{refundResult.card_last_four}
                          </div>
                        )}
                      </>
                    )}
                    <div style={styles.refundResultNumber}>
                      Refund #{refundResult.refund_number}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={styles.refundFailIcon}>&#10007;</div>
                    <h2 style={styles.refundResultTitle}>Refund {refundResult.status === 'declined' ? 'Declined' : 'Failed'}</h2>
                  </>
                )}

                <div style={styles.refundResultActions}>
                  {refundReceipt && (
                    <button
                      onClick={handlePrintRefundReceipt}
                      disabled={printing}
                      style={{
                        ...styles.printBtn,
                        opacity: printing ? 0.6 : 1,
                      }}
                    >
                      {printing ? 'PRINTING...' : 'PRINT REFUND RECEIPT'}
                    </button>
                  )}
                  <button onClick={handleRefundDone} style={styles.newSaleBtn}>
                    DONE
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Purchase History Drawer — right-side slide-over. Renders the
          drawer payload from /pos/customers/purchase-history/. Same shape
          for BC members and unmatched walk-ins; visitor flavour adds
          shadow_points + invite_priority. */}
      {showPurchaseDrawer && (
        <div
          onClick={() => setShowPurchaseDrawer(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.4)', zIndex: 1500,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: 0, right: 0, bottom: 0,
              width: 'min(480px, 100vw)',
              background: '#FFFFFF',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', padding: '14px 16px',
              borderBottom: '1px solid #E5E7EB',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Purchase History</div>
                {purchaseHistory?.subject && (
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    {purchaseHistory.subject.name}
                    {purchaseHistory.kind === 'visitor' && ' (walk-in)'}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowPurchaseDrawer(false)}
                style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6B7280' }}
              >×</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {purchaseHistoryLoading ? (
                <div style={{ color: '#6B7280' }}>Loading purchase history…</div>
              ) : purchaseHistoryKey && purchaseHistoryError[purchaseHistoryKey] ? (
                // Fetch error path — surfaces backend/network failures
                // that previously silently rendered as "No data yet."
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 8,
                  padding: 12,
                  color: '#991B1B',
                  fontSize: 13,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Couldn't load purchase history
                  </div>
                  <div style={{ marginBottom: 10, fontSize: 12 }}>
                    {purchaseHistoryError[purchaseHistoryKey]}
                  </div>
                  <button
                    onClick={openPurchaseDrawer}
                    style={{
                      background: '#DC2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : !purchaseHistory ? (
                // True "no data" — fetch hasn't run yet (drawer just
                // opened) or the cashier opened it before clicking
                // the pill. Distinct from the error path above.
                <div style={{ color: '#6B7280' }}>No data yet.</div>
              ) : (
                <>
                  {/* Top-line summary */}
                  <div style={{
                    background: '#F0FDF4', border: '1px solid #86EFAC',
                    borderRadius: 8, padding: 12, marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.5 }}>
                      <strong>{purchaseHistory.visits}</strong> visit{purchaseHistory.visits === 1 ? '' : 's'} ·
                      <strong style={{ marginLeft: 4 }}>${purchaseHistory.total_spent?.toFixed(2)}</strong> lifetime
                    </div>
                    {purchaseHistory.last_visit_days_ago != null && (
                      <div style={{ fontSize: 12, color: '#15803D', marginTop: 4 }}>
                        Last visit {purchaseHistory.last_visit_days_ago} day{purchaseHistory.last_visit_days_ago === 1 ? '' : 's'} ago
                        {purchaseHistory.avg_days_between_visits && (
                          <> · usually every {Math.round(purchaseHistory.avg_days_between_visits)} days</>
                        )}
                        {purchaseHistory.churn_flag && (
                          <span style={{ marginLeft: 6, color: '#92400E', fontWeight: 600 }}>
                            ⚠ stale
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Points or shadow points */}
                  {purchaseHistory.kind === 'member' ? (
                    <div style={{ marginBottom: 16, fontSize: 13 }}>
                      <strong>{purchaseHistory.points_balance}</strong> points balance ·{' '}
                      {purchaseHistory.redeemable_rewards > 0 ? (
                        <span style={{ color: '#92400E' }}>
                          ${purchaseHistory.store_credit_available_dollars} store credit ready
                        </span>
                      ) : (
                        <span style={{ color: '#6B7280' }}>
                          {500 - (purchaseHistory.points_balance % 500)} pts to next $10
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      marginBottom: 16, padding: 10,
                      background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 6,
                      fontSize: 13, color: '#78350F',
                    }}>
                      Would have <strong>{purchaseHistory.shadow_points}</strong> points if they joined today
                      {purchaseHistory.shadow_rewards > 0 && (
                        <> — <strong>${purchaseHistory.shadow_credit_dollars}</strong> in store credit waiting</>
                      )}
                      {purchaseHistory.invite_priority && (
                        <span style={{ display: 'inline-block', marginLeft: 8, background: '#F59E0B', color: 'white', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                          INVITE PRIORITY
                        </span>
                      )}
                    </div>
                  )}

                  {/* Usual basket chips */}
                  {purchaseHistory.usual_basket?.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', fontWeight: 600 }}>
                        Usually buys
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                        {purchaseHistory.usual_basket.map((p: any, idx: number) => (
                          <span
                            key={idx}
                            style={{
                              background: '#F3F4F6',
                              borderRadius: 999,
                              padding: '4px 10px',
                              fontSize: 12,
                              color: '#1F2937',
                            }}
                          >
                            {p.product_name} <span style={{ color: '#9CA3AF' }}>· {p.visits}×</span>
                          </span>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Recent baskets, expandable */}
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', fontWeight: 600 }}>
                    Recent baskets
                  </div>
                  {purchaseHistory.recent_baskets?.length === 0 ? (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>No recorded baskets yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {purchaseHistory.recent_baskets?.map((b: any, idx: number) => {
                        const key = `${b.source}-${b.transaction_number || idx}`;
                        const isOpen = expandedBasketKeys.has(key);
                        return (
                          <div key={key} style={{
                            background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden',
                          }}>
                            <div
                              onClick={() => toggleBasketKey(key)}
                              style={{
                                display: 'flex', justifyContent: 'space-between',
                                padding: '8px 10px', cursor: 'pointer', fontSize: 13,
                              }}
                            >
                              <span style={{ color: '#374151' }}>
                                {isOpen ? '▼' : '▶'} {b.date?.substring(0, 10)} · {b.source_label}
                              </span>
                              <span style={{ fontWeight: 600, color: '#166534' }}>${b.total?.toFixed(2)}</span>
                            </div>
                            {isOpen && (
                              <div style={{ padding: '6px 12px 10px 24px', borderTop: '1px solid #E5E7EB', background: '#FFFFFF' }}>
                                {b.items.length === 0 ? (
                                  <div style={{ color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>
                                    No line items recorded
                                  </div>
                                ) : (
                                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#1F2937' }}>
                                    {b.items.map((it: any, ii: number) => (
                                      <li key={ii} style={{ marginBottom: 2 }}>
                                        {it.product_name}
                                        <span style={{ color: '#6B7280' }}>
                                          {' '}× {it.quantity}{it.unit_price > 0 ? ` @ $${it.unit_price.toFixed(2)}` : ''}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* "View full profile →" — opens admin detail in new tab */}
                  {(selectedCustomer?.id || selectedCustomer?.unmatched_visitor_id) && (
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                      <a
                        href={
                          selectedCustomer?.is_unmatched && selectedCustomer.unmatched_visitor_id
                            ? `/admin/bridge-club/walk-ins/${selectedCustomer.unmatched_visitor_id}`
                            : `/admin/bridge-club/members/${selectedCustomer.id}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2D5A27', fontSize: 13, fontWeight: 600 }}
                      >
                        View full profile →
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles - "65-year-old easy" with large touch targets and clear text
const styles: { [key: string]: React.CSSProperties } = {
  page: {
    height: '100vh',
    width: '100vw',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    padding: '0.5rem 0.5rem 0.25rem',
    width: '100%',
    overflow: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  loadingText: {
    fontSize: '1.25rem',
    color: '#666',
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '2rem',
    width: '90%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  modalTitle: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: '0 0 1rem 0',
  },
  modalText: {
    fontSize: '1.125rem',
    color: '#666',
    marginBottom: '1.5rem',
  },
  priceText: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    marginBottom: '1rem',
  },
  inputLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem',
  },
  calculatedTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '1.25rem',
  },
  modalBtnRow: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  primaryBtn: {
    flex: 1,
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '56px',
  },
  secondaryBtn: {
    flex: 1,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '56px',
  },
  cashInput: {
    width: '100%',
    padding: '1rem',
    fontSize: '2rem',
    textAlign: 'center',
    border: '2px solid #ccc',
    borderRadius: '12px',
    marginBottom: '1rem',
  },

  // Error banner
  errorBanner: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.125rem',
  },
  errorClose: {
    background: 'none',
    border: 'none',
    color: '#c62828',
    fontSize: '1.25rem',
    cursor: 'pointer',
    padding: '0.5rem',
  },

  // Checkout layout - 70/30 split
  checkoutLayout: {
    display: 'grid',
    gridTemplateColumns: '7fr 3fr',
    gap: '0.5rem',
    height: '100%',
    overflow: 'hidden',
  },

  // Products area (left 70%)
  productsArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    overflow: 'hidden',
    minWidth: 0,
  },
  barcodeForm: {
    display: 'flex',
    gap: '0.5rem',
  },
  barcodeInput: {
    flex: 1,
    padding: '0.5rem 0.75rem',
    fontSize: '1rem',
    border: '2px solid #ccc',
    borderRadius: '12px',
    minHeight: '44px',
  },
  scanBtn: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '0 1rem',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minWidth: '80px',
    minHeight: '44px',
  },
  searchBtn: {
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    padding: '0 1rem',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minWidth: '80px',
    minHeight: '44px',
  },
  fullscreenBtn: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1.25rem',
    cursor: 'pointer',
    width: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Category Tabs
  categoryTabs: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    padding: '0.25rem 0',
  },
  categoryTab: {
    padding: '0.75rem 1.25rem',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '60px',
    minWidth: '80px',
    transition: 'all 0.15s ease',
  },

  // Quick buttons section (touch-friendly)
  quickButtonsSection: {
    backgroundColor: '#f0f7f0',
    borderRadius: '12px',
    padding: '0.75rem',
    border: '2px solid #c8e6c9',
  },
  quickButtonsHeader: {
    marginBottom: '0.5rem',
  },
  quickLabel: {
    fontSize: '0.875rem',
    fontWeight: '700',
    color: '#2e7d32',
    letterSpacing: '0.5px',
  },
  quickButtonsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '0.5rem',
  },
  quickButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.75rem',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    minHeight: '70px',
    transition: 'transform 0.1s, box-shadow 0.1s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  quickButtonName: {
    fontSize: '0.9rem',
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 1.2,
  },
  quickButtonPrice: {
    fontSize: '1rem',
    fontWeight: 'bold',
    marginTop: '0.25rem',
  },

  // Product Grid Section
  productGridSection: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '0.5rem',
    overflow: 'auto',
  },
  loadingProducts: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: '#666',
    fontSize: '1.125rem',
  },
  noProducts: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: '#999',
    fontSize: '1.125rem',
  },
  productGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '0.5rem',
  },
  productCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.75rem 0.5rem',
    border: '2px solid #ddd',
    borderRadius: '10px',
    cursor: 'pointer',
    minHeight: '70px',
    backgroundColor: '#fff',
    transition: 'transform 0.1s, box-shadow 0.1s, border-color 0.1s',
  },
  productName: {
    fontSize: '0.85rem',
    fontWeight: '600',
    textAlign: 'center',
    color: '#333',
    lineHeight: 1.2,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  productPrice: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    marginTop: '0.25rem',
  },
  perLb: {
    fontSize: '0.75rem',
    color: '#ff9800',
    marginLeft: '2px',
  },
  sectionLabel: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666',
    marginBottom: '0.75rem',
    textTransform: 'uppercase',
  },

  // Cart area (right 30%)
  cartArea: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  // Scroll region — customer info + bins + loyalty + banners + cart items
  // all scroll together here, so cartFooter (totals + PAY) stays anchored
  // at the bottom of cartArea no matter how tall the customer panel grows.
  // overscrollBehavior:contain stops scroll-chaining to the page; the
  // -webkit prop enables momentum scroll on the Ubuntu/Chrome kiosk.
  cartScrollRegion: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
  },
  cartHeader: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #e0e0e0',
  },
  cartTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  transactionNumber: {
    fontSize: '1rem',
    color: '#666',
  },
  emptyCart: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
  },
  emptyCartText: {
    fontSize: '1.125rem',
    color: '#999',
  },
  newSaleBtn: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '56px',
    minWidth: '200px',
  },

  // Cart items - now lives inside cartScrollRegion, so it sizes to content
  // and scrolls via the shared region (no own flex/overflow).
  cartItems: {
    marginBottom: '0',
  },
  // Compact cart item - two lines max
  cartItem: {
    padding: '6px 8px',
    borderBottom: '1px solid #eee',
  },
  // First row: name and price
  cartItemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  cartItemName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: '8px',
  },
  cartItemUnit: {
    fontWeight: 'normal',
    color: '#666',
    fontSize: '12px',
  },
  cartItemTotal: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1a4d1a',
    whiteSpace: 'nowrap',
  },
  // Second row: qty controls
  cartItemControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cartItemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  qtyBtn: {
    width: '28px',
    height: '28px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    backgroundColor: '#f5f5f5',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
  },
  qtyDisplay: {
    width: '32px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  qtyInput: {
    width: '40px',
    height: '28px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    border: '1px solid #ccc',
    borderRadius: '4px',
    MozAppearance: 'textfield',
    padding: '2px',
  },
  qtyInputWide: {
    width: '60px',
    height: '28px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    border: '1px solid #ccc',
    borderRadius: '4px',
    MozAppearance: 'textfield',
    padding: '2px',
  },
  removeBtn: {
    width: '28px',
    height: '28px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Discount & Tax Exempt styles
  taxExemptBadge: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontSize: '9px',
    fontWeight: 'bold',
    padding: '1px 4px',
    borderRadius: '3px',
    marginLeft: '4px',
    verticalAlign: 'middle',
    letterSpacing: '0.3px',
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: '#999',
    fontSize: '12px',
    fontWeight: 'normal',
  },
  discountInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2px',
  },
  discountInfoText: {
    fontSize: '11px',
    color: '#e65100',
    fontWeight: '500',
  },
  discountRemoveBtn: {
    background: 'none',
    border: 'none',
    color: '#c62828',
    fontSize: '10px',
    cursor: 'pointer',
    padding: '0 2px',
  },
  itemDiscountBtn: {
    width: '28px',
    height: '28px',
    border: '1px solid #ff9800',
    borderRadius: '6px',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
  },
  cartToolbar: {
    display: 'flex',
    gap: '6px',
    marginBottom: '6px',
  },
  taxExemptBtn: {
    flex: 1,
    padding: '6px 8px',
    border: '1px solid #4CAF50',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#4CAF50',
    fontSize: '11px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  taxExemptBtnActive: {
    flex: 1,
    padding: '6px 8px',
    border: '1px solid #4CAF50',
    borderRadius: '6px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cartDiscountBtn: {
    flex: 1,
    padding: '6px 8px',
    border: '1px solid #ff9800',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#e65100',
    fontSize: '11px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  discountLabel: {
    color: '#e65100',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  discountRemoveBtnSmall: {
    background: 'none',
    border: 'none',
    color: '#c62828',
    fontSize: '11px',
    cursor: 'pointer',
    padding: '0 2px',
    lineHeight: 1,
  },
  discountTypeToggle: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  discountTypeBtn: {
    flex: 1,
    padding: '0.75rem',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    backgroundColor: '#fff',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#666',
  },
  discountTypeActive: {
    flex: 1,
    padding: '0.75rem',
    border: '2px solid #ff9800',
    borderRadius: '10px',
    backgroundColor: '#fff3e0',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#e65100',
  },

  // Cart footer - anchored at the bottom of cartArea as a sibling of
  // cartScrollRegion. borderTop + backgroundColor keep it visually
  // anchored against the scrolling content above it.
  cartFooter: {
    borderTop: '2px solid #333',
    backgroundColor: '#fff',
    paddingTop: '8px',
  },
  // Cart totals - compact
  cartTotals: {
    marginBottom: '8px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#666',
    marginBottom: '2px',
  },
  grandTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    marginTop: '4px',
    paddingTop: '4px',
    borderTop: '1px solid #ccc',
  },

  // Cart actions - compact
  cartActions: {
    display: 'flex',
    gap: '8px',
  },
  voidBtn: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '2px solid #c62828',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    minWidth: '70px',
    minHeight: '48px',
  },
  payBtn: {
    flex: 1,
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '48px',
  },

  // Search view
  searchView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  searchHeader: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  backBtn: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '1rem 1.5rem',
    borderRadius: '12px',
    fontSize: '1.125rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  searchInput: {
    flex: 1,
    padding: '1rem',
    fontSize: '1.25rem',
    border: '2px solid #ccc',
    borderRadius: '12px',
  },
  categoryFilter: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginBottom: '1rem',
  },
  categoryBtn: {
    padding: '0.75rem 1.25rem',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  searchResults: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: '12px',
    overflow: 'auto',
  },
  noResults: {
    padding: '2rem',
    textAlign: 'center',
    color: '#999',
    fontSize: '1.125rem',
  },
  searchResultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    backgroundColor: '#fff',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  },
  resultInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  resultName: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333',
  },
  resultSku: {
    fontSize: '0.875rem',
    color: '#999',
  },
  resultPrice: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  byocTag: {
    fontSize: '0.875rem',
    color: '#ff9800',
    marginLeft: '0.25rem',
  },

  // Payment view
  paymentView: {
    maxWidth: '600px',
    margin: '0 auto',
    height: '100%',
    overflow: 'auto',
  },
  paymentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  paymentTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  // Receipt options
  receiptOptions: {
    display: 'flex',
    gap: '1.5rem',
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },

  // Bin loading section
  binLoadSection: {
    backgroundColor: '#e8f5e9',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1rem',
    border: '2px solid #a5d6a7',
  },
  binLoadHeader: {
    borderBottom: '1px solid #a5d6a7',
    paddingBottom: '0.5rem',
    marginBottom: '0.75rem',
  },
  binLoadTitle: {
    fontSize: '1rem',
    fontWeight: '700',
    color: '#2e7d32',
    letterSpacing: '0.5px',
  },
  binLoadList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  binLoadItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    flexWrap: 'wrap',
  },
  binCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    minWidth: '150px',
  },
  binName: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#333',
  },
  binQuantityInput: {
    width: '70px',
    padding: '0.5rem',
    fontSize: '1rem',
    border: '2px solid #ccc',
    borderRadius: '8px',
    textAlign: 'center',
  },
  binLbs: {
    fontSize: '0.9rem',
    color: '#666',
  },
  binProduct: {
    fontSize: '0.85rem',
    color: '#1a4d1a',
    fontStyle: 'italic',
    marginLeft: 'auto',
  },

  paymentTabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  paymentTab: {
    flex: 1,
    padding: '1rem',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cashPayment: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  cashInputSection: {
    marginBottom: '1.5rem',
  },
  quickCashGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  quickCashBtn: {
    padding: '1rem',
    border: '2px solid #1a4d1a',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  exactBtn: {
    padding: '1rem',
    border: '2px solid #1565c0',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#1565c0',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  clearBtn: {
    padding: '1rem',
    border: '2px solid #c62828',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#c62828',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  changeDisplay: {
    backgroundColor: '#e8f5e9',
    padding: '1rem',
    borderRadius: '12px',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e7d32',
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  completePaymentBtn: {
    width: '100%',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    padding: '1.25rem',
    borderRadius: '12px',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cardPayment: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    textAlign: 'center',
  },
  cardInstructions: {
    fontSize: '1.25rem',
    color: '#333',
    marginBottom: '1rem',
  },
  cardNote: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '2rem',
    lineHeight: 1.6,
  },
  activateTerminalBtn: {
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    padding: '1.25rem 2rem',
    borderRadius: '12px',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minWidth: '250px',
  },
  cardWaiting: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
  },
  spinnerLarge: {
    width: '60px',
    height: '60px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #1565c0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  cardApprovedIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '3rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto',
  },
  cardApprovedText: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  cardTypeLabel: {
    fontSize: '1.125rem',
    color: '#666',
  },
  cardTransitioning: {
    fontSize: '1rem',
    color: '#999',
    fontStyle: 'italic',
  },
  cardStatus: {
    fontSize: '1.25rem',
    color: '#333',
  },
  cancelCardBtn: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '12px',
    fontSize: '1.125rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },

  // Receipt view
  receiptView: {
    maxWidth: '500px',
    margin: '0 auto',
    textAlign: 'center',
    height: '100%',
    overflow: 'auto',
  },
  receiptSuccess: {
    marginBottom: '2rem',
  },
  checkmark: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '3rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 1rem auto',
  },
  successTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#2e7d32',
    margin: 0,
  },
  changeAmount: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '12px',
  },
  receiptPreview: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    textAlign: 'left',
    marginBottom: '1.5rem',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  receiptHeader: {
    textAlign: 'center',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px dashed #ccc',
  },
  receiptItems: {
    marginBottom: '1rem',
  },
  receiptItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
  },
  receiptTotals: {
    borderTop: '1px dashed #ccc',
    paddingTop: '1rem',
  },
  receiptTotalLine: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.25rem',
  },
  receiptGrandTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: '1px dashed #ccc',
  },
  receiptBinsSection: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '8px',
    border: '1px solid #a5d6a7',
  },
  receiptBinsHeader: {
    fontSize: '0.9rem',
    fontWeight: '700',
    color: '#2e7d32',
    marginBottom: '0.5rem',
    letterSpacing: '0.5px',
  },
  receiptBinItem: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    fontSize: '0.85rem',
    color: '#333',
    marginBottom: '0.25rem',
  },
  receiptBinName: {
    fontWeight: '600',
  },
  receiptBinAdded: {
    color: '#2e7d32',
    fontWeight: '600',
  },
  receiptBinLevel: {
    color: '#666',
    fontStyle: 'italic',
  },
  receiptActions: {
    display: 'flex',
    gap: '1rem',
  },
  printBtn: {
    flex: 1,
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    padding: '1rem',
    borderRadius: '12px',
    fontSize: '1.125rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  printError: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  drawerBtn: {
    marginTop: '1rem',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: '1px solid #ddd',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
  },

  // Customer lookup styles
  closeShiftBtn: {
    backgroundColor: '#ff9800',
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.5rem',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  refundBtn: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.5rem',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  customerBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '1px dashed #ccc',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '0.75rem',
    fontSize: '1rem',
  },
  customerIcon: {
    fontSize: '1.25rem',
  },
  customerMemberCode: {
    marginLeft: 'auto',
    fontSize: '0.875rem',
    color: '#666',
  },
  binsPreview: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    padding: '0.75rem',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    alignItems: 'center',
  },
  binsPreviewLabel: {
    fontWeight: '600',
    color: '#1565c0',
    marginRight: '0.5rem',
  },
  binPreviewItem: {
    backgroundColor: '#fff',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    border: '1px solid #90caf9',
    color: '#1976d2',
  },
  customerResults: {
    maxHeight: '250px',
    overflowY: 'auto' as const,
    marginBottom: '1rem',
  },
  customerResult: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '0.5rem',
    textAlign: 'left' as const,
  },
  customerInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  customerName: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333',
  },
  customerCode: {
    fontSize: '0.875rem',
    color: '#666',
  },
  customerContact: {
    fontSize: '0.875rem',
    color: '#999',
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  memberSince: {
    color: '#666',
    fontStyle: 'italic',
  },

  // Session summary styles
  summarySection: {
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  summaryTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666',
    margin: '0 0 0.75rem 0',
    textTransform: 'uppercase' as const,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
    fontSize: '1rem',
  },

  // Refund styles
  refundView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '700px',
    margin: '0 auto',
    padding: '1rem',
    overflow: 'auto',
  },
  refundHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  refundBackBtn: {
    backgroundColor: '#666',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '48px',
  },
  refundTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#d32f2f',
    margin: 0,
  },
  refundLookupSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '2rem',
  },
  refundLabel: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333',
  },
  refundLookupRow: {
    display: 'flex',
    gap: '0.75rem',
  },
  refundInput: {
    flex: 1,
    padding: '0.875rem 1rem',
    fontSize: '1.25rem',
    border: '2px solid #ccc',
    borderRadius: '8px',
    minHeight: '48px',
  },
  refundLookupBtn: {
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    padding: '0.875rem 2rem',
    borderRadius: '8px',
    fontSize: '1.125rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '48px',
    whiteSpace: 'nowrap',
  },
  refundError: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
  },
  refundReviewSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  refundTxnSummary: {
    backgroundColor: '#f5f5f5',
    padding: '1rem',
    borderRadius: '8px',
    border: '1px solid #ddd',
  },
  refundTxnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  refundTxnNumber: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#333',
  },
  refundTxnDate: {
    fontSize: '1rem',
    color: '#666',
  },
  refundTxnCashier: {
    fontSize: '0.9rem',
    color: '#888',
    marginTop: '0.25rem',
  },
  refundWarningBanner: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    border: '1px solid #ffcc80',
  },
  refundInfoBanner: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    border: '1px solid #90caf9',
  },
  refundItemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    maxHeight: '250px',
    overflow: 'auto',
  },
  refundItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: '#fff',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    minHeight: '48px',
  },
  refundItemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  refundItemCheckbox: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  refundItemName: {
    fontSize: '1rem',
    fontWeight: '500',
  },
  refundItemDetails: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    fontSize: '0.9rem',
    color: '#666',
  },
  refundItemPrice: {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '1rem',
  },
  refundTypeToggle: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.5rem',
  },
  refundTypeBtn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    border: '2px solid #ccc',
    borderRadius: '12px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
    minHeight: '72px',
    color: '#333',
  },
  refundTypeBtnActive: {
    borderColor: '#d32f2f',
    backgroundColor: '#ffebee',
    color: '#d32f2f',
  },
  refundTypeAmount: {
    fontSize: '0.85rem',
    fontWeight: 'normal',
    marginTop: '0.25rem',
    opacity: 0.8,
  },
  refundCustomAmountSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  refundReasonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  refundProcessBtn: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '12px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    minHeight: '56px',
    marginTop: '0.5rem',
  },
  refundCancelBtn: {
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #ccc',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
    minHeight: '48px',
    textAlign: 'center',
  },
  refundResultSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '3rem 2rem',
    flex: 1,
  },
  refundSuccessIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    color: '#fff',
    fontSize: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  refundFailIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#d32f2f',
    color: '#fff',
    fontSize: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  refundResultTitle: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  refundCashAmount: {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: '#d32f2f',
    padding: '1rem 2rem',
    backgroundColor: '#ffebee',
    borderRadius: '12px',
    border: '2px solid #d32f2f',
  },
  refundResultAmount: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#333',
  },
  refundResultCard: {
    fontSize: '1.125rem',
    color: '#666',
  },
  refundResultNumber: {
    fontSize: '1rem',
    color: '#888',
    marginTop: '0.5rem',
  },
  refundResultActions: {
    display: 'flex',
    gap: '1rem',
    marginTop: '2rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
};

// Add keyframe animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default StorePOS;
